import { formatDate, CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { catchError, finalize, forkJoin, of } from 'rxjs';

// Models and Services
import { ShipmentDeliveryAttachment } from '../models/shipment-delivery.attachment';
import { ShipmentDelivery } from '../models/delivery/shipment-delivery.model';
import { ShipmentDeliveryActionData } from '../models/shipment-action-data.model';
import { Shipment } from '../models/shipment';
import { WarehousingShipmentInboundRequest } from '../../warehousing/warehousing.model';
import { WarehousingService } from '../../warehousing/warehousing.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { DeliveryCashBoxOption, PettyCashService, preferredDeliveryCashBoxId } from '../../accounts/workforce-cash/petty-cash.service';
import { GeoFireModel } from '../../models/vehicle/geo-fire.model';
import { ShipmentFileUploadComponent } from 'src/app/helpers/shipment-file-upload/shipment-file-upload.component';
import { ShipmentFileDownloadUrlCallback } from 'src/app/services/google/google-cloud-storage.service';
import { HistoricalDeliveryCompletionRequest, ShipmentContainerOption, ShipmentPayloadLineCommandResult, ShipmentService } from '../shipment.service';
import { ShipmentChargeLine, ShipmentChargeLineBulkCreateRequest } from '../models/charge-line/shipment-charge-line';
import { GeoAddress } from '../../models/common/geo-address.model';
import { ShipmentDeliveryStop } from '../models/workflow/shipment-delivery-stop.model';
import { ShipmentWaypoint } from '../models/waypoint/shipment.waypoint';
import { WaypointDialogComponent } from '../waypoint-dialog/waypoint-dialog.component';
import { ShipmentDeliveryOperationStatus, ShipmentOperationStopStatus, ShipmentOperationTimelineEvent } from '../models/workflow/shipment-workflow.model';
import { ShipmentRemark } from '../models/shipment-remark.model';
import { ShipmentRemarksFeed } from '../models/shipment-remarks-feed.model';
import { ShipmentWaybillSummary } from '../models/shipment-waybill-summary.model';
import { Driver } from '../../fleet/driver/driver.model';
import { DriverService } from '../../fleet/driver/driver.service';
import { Vehicle } from '../../fleet/vehicle/vehicle.model';
import { VehicleService } from '../../fleet/vehicle/vehicle.service';
import { PayloadItemView } from '../../models/meta-params/payload-item-view';
import { PayloadType } from '../../models/meta-params/payload-type';
import { UOM } from '../../models/meta-params/uom';
import { Weight } from '../../models/meta-params/weight';
import { PayloadLineCommandDialogComponent } from '../../shared/payload-line-command-dialog/payload-line-command-dialog.component';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { fleetResourceAgencyLabel, fleetResourceOwnerLabel, fleetResourceOwnershipTypeLabel } from '../../shared/utils/fleet-resource-ownership.util';
import { DeliveryNoteLogisticsProject } from '../models/delivery-note-logistics-project';
import {
    ContainerCaseDeliveryContext,
    ContainerCaseFromShipmentDeliveryResult,
    ContainerTrackingService
} from '../../container-tracking/container-tracking.service';
import { ContainerTransportLeg } from '../../container-tracking/models/container-transport-leg.model';
import { DeliveryContainerCaseDialogComponent } from './delivery-container-case-dialog.component';
import { DeliveryTrackingPanelComponent, DeliveryTrackingSummary } from './tracking/delivery-tracking-panel.component';
import { PayloadLedgerComponent, PayloadLedgerConfiguration } from '../payload-ledger/payload-ledger.component';
import { DeliveryCancellationDialogComponent } from './dialogs/delivery-cancellation-dialog.component';
import { HistoricalCompletionDialogComponent } from './dialogs/historical-completion-dialog.component';

// PrimeNG & Google Maps
import { MenuItem, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

// Standalone Component Imports
import { ButtonModule } from "primeng/button";
import { SplitButtonModule } from 'primeng/splitbutton';
import { ToastModule } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { ImageModule } from 'primeng/image';
import { AccordionModule } from 'primeng/accordion';
import { GalleriaModule } from 'primeng/galleria';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { EditorModule } from 'primeng/editor';
import { ProgressBarModule } from 'primeng/progressbar';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TextareaModule } from 'primeng/textarea';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ChargeLineDialogComponent } from '../charge-line-dialog/charge-line-dialog.component';
import { TabsModule } from 'primeng/tabs';
import { VehicleKeyPack } from 'src/app/services/vehicle-key-pack.service';
import { CardModule } from "primeng/card";
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { calculateDeliveryOperationProgress } from '../utils/delivery-operation-progress.util';
import { AuditStamp } from '../../shared/audit/audit.models';
import {
    isHistoricalWaybillAttachment,
    latestHistoricalWaybillAttachment
} from '../utils/shipment-waybill.utils';
import { TranslocoPipe } from '@jsverse/transloco';
import { LocaleService } from '../../i18n/locale.service';
import { FieldResourcesPanelComponent } from '../../field-work/field-resources-panel.component';
import { FieldWorkLocationOption, FieldWorkPackageCreateDialogComponent } from '../../field-work/field-work-package-create-dialog.component';
import { FieldWorkPackage } from '../../field-work/field-work.models';
import { FieldWorkService } from '../../field-work/field-work.service';
import { DeliveryTripMoneyRequest, DriverPayTripView, WorkforceCashAdvance, WorkforceDeliveryFinanceSummary } from '../../accounts/workforce-cash/workforce-cash.models';
import { WorkforceCashService } from '../../accounts/workforce-cash/workforce-cash.service';
import { WorkforceFinanceCurrencyService } from '../../accounts/workforce-cash/workforce-finance-currency.service';
import { AttachmentPreviewComponent } from '../../shared/attachment-preview/attachment-preview.component';

interface OperationalTimelineGroup {
    key: string;
    title: string;
    subtitle: string;
    events: ShipmentOperationTimelineEvent[];
}


@Component({
	selector: 'app-view-delivery',
	templateUrl: './delivery.component.html',
	styleUrls: ['./delivery.component.css', '../../shared/party-quick-create/party-quick-create-dialog.component.css'],
	standalone: true,
	imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ToastModule,
    BreadcrumbModule,
    TagModule,
    TimelineModule,
    ButtonModule,
    SplitButtonModule,
    TabsModule,
    BadgeModule,
    AvatarModule,
    ImageModule,
    AccordionModule,
    GalleriaModule,
    TableModule,
    TooltipModule,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    EditorModule,
    ProgressBarModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    TextareaModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    ChargeLineDialogComponent,
    CardModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PayloadLineCommandDialogComponent,
    LazyLocationPickerDirective,
    TranslocoPipe,
    FieldResourcesPanelComponent,
    FieldWorkPackageCreateDialogComponent
    ,WaypointDialogComponent,
    DeliveryContainerCaseDialogComponent,
    DeliveryTrackingPanelComponent,
    PayloadLedgerComponent,
    DeliveryCancellationDialogComponent,
    HistoricalCompletionDialogComponent,
    AttachmentPreviewComponent
],
	providers: [MessageService, DialogService, ShipmentService]
})
export class DeliveryComponent implements OnInit {
    private readonly warehousingService = inject(WarehousingService);
    shipmentInboundRequests: WarehousingShipmentInboundRequest[] = [];
    protected readonly app_permissions = app_permissions;
    private readonly localeService = inject(LocaleService);
    private readonly fieldWorkService = inject(FieldWorkService);
    private readonly workforceFinance = inject(WorkforceCashService);
    private readonly workforceCurrencies = inject(WorkforceFinanceCurrencyService);
    readonly workforceCurrencyOptions$ = this.workforceCurrencies.currencyOptions$;
    readonly cashPurposeOptions = [
        { label: 'No cash', value: 'NONE' },
        { label: 'Company expense float', value: 'COMPANY_EXPENSE_FLOAT' },
        { label: 'Advance against pay', value: 'ADVANCE_AGAINST_PAY' }
    ];
    readonly driverPayMethods = [
        { label: 'No driver pay', value: 'NONE' },
        { label: 'Fixed trip pay', value: 'FIXED_TRIP' },
        { label: 'Destination trip pay', value: 'CITY_PAIR_TRIP' },
        { label: 'Pay per km', value: 'PER_KM' }
    ];
    readonly payDistanceSources = [
        { label: 'Shipment distance', value: 'PLANNED_ROUTE' },
        { label: 'GPS device odometer', value: 'GPS_ODOMETER' },
        { label: 'Start/end odometer photos', value: 'PHOTO_ODOMETER' }
    ];
    private requestedDeliveryTabKey: string | null = null;
    private readonly deliveryTabKeys = [
        { key: 'control', value: 0 },
        { key: 'timeline', value: 9 },
        { key: 'tracking', value: 7 },
        { key: 'crew-asset', value: 1 },
        { key: 'files', value: 2 },
        { key: 'payload', value: 8 },
        { key: 'containers', value: 10 },
        { key: 'field-work', value: 11 },
        { key: 'charges', value: 3 },
        { key: 'pod', value: 5 },
        { key: 'notes', value: 6 }
    ] as const;
    get deliveryPayloadLedgerConfiguration(): PayloadLedgerConfiguration {
        const isCancelled = this.isCancelled();
        return {
            title: 'Payload Lines',
            emptyDescription: 'This delivery has no payload details yet.',
            releaseRoutePrefix: '/transport-planning/releases',
            orchestrationRoutePrefix: '/transport-planning/orchestrations',
            allowMutations: !isCancelled,
            allowExtraction: !isCancelled
        };
    }

	deliveryId: number;
	private deliveryLoadRequestToken = 0;
	shipment: Shipment;
	shipmentDelivery: ShipmentDelivery;
	shipmentDeliveryAttachments: ShipmentDeliveryAttachment[] = [];
	deliveryFilesCount: number = 0;
	isLoading: boolean = true;
    deliveryNotesLoading = false;
    deliveryNotesError = false;
    private deliveryNotesLoaded = false;
    deliveryWaybillsLoading = false;
    deliveryWaybillsError = false;
    deliveryContainerCasesError = false;
    deliveryFieldWorkError = false;
    deliveryFinanceError = false;
    private deliveryFinanceRequested = false;
    payloadExtractionInProgress = false;
    returnDestinationDialogVisible = false;
    returnDestinationSaving = false;
    returnDestinationError = '';
    selectedReturnStop?: ShipmentDeliveryStop;
    returnDestinationLocationId?: number;
	breadcrumbItems: MenuItem[];
	homeBreadcrumb: MenuItem;
    operationStatus: ShipmentDeliveryOperationStatus | null = null;
    actionData = signal<ShipmentDeliveryActionData | null>(null);
    liveTrackingDistance = signal<string | null>(null);
    liveTrackingEta = signal<string | null>(null);
    trackingStatus = signal<string | null>(null);
    alerts = signal<string[]>([]);
    hasAlerts = signal<boolean>(false);
	// Timeline data
    operationTimelineEvents: ShipmentOperationTimelineEvent[] = [];
    deliveryStops: ShipmentDeliveryStop[] = [];
    historicalWaybillDialogVisible = false;
    historicalWaybillStopId: number | null = null;
    historicalWaybillAttachmentId: number | null = null;
    historicalWaybillNumber = '';
    historicalWaybillExpected: string | null = null;
    historicalWaybillError = '';
    historicalWaybillBusy = false;
    historicalFilesLoading = false;
    historicalFilesReady = false;
    historicalNumberLoading = false;
    deliveryOnlyStopDialogVisible = false;
    deliveryOnlyStopSaving = false;
    editingDeliveryOnlyStop?: ShipmentDeliveryStop;
    deliveryOnlyStopWaypoint: ShipmentWaypoint = new ShipmentWaypoint();


	// Component properties
	chargeLineDialogVisible: boolean = false;
	selectedChargeLine: ShipmentChargeLine;
	saveChargeLineInProgress: boolean = false;
	chargeTypeToBeUpdated: string = '';
	updateChargesDialogVisible: boolean = false;
	updatedCharge: number = 0;
    updateChargesDialogHeader: string = '';
    updateChargesInProgress: boolean = false;
    waybillReprintInProgress: boolean = false;
    deliveryNoteGenerationInProgress = false;
    crewAssetDialogVisible: boolean = false;
    crewAssetSaveInProgress: boolean = false;
    crewAssetOptionsLoading: boolean = false;
    availableDrivers: Driver[] = [];
    availableVehicles: Vehicle[] = [];
    selectedReplacementDriver: Driver | null = null;
    selectedReplacementVehicle: Vehicle | null = null;
    crewAssetTransportCostAmount: number | null = null;
    crewAssetNoteHtml: string = '';
    deliveryCancellationDialogVisible = false;
    deliveryCancellationInProgress = false;
    deliveryCancellationReason = '';
    deliveryRemark: ShipmentRemark = {};
    deliveryRemarksHtml = '';
    deliveryRemarksFeed: ShipmentRemarksFeed = { entries: [] };
    deliveryWaybillSummary: ShipmentWaybillSummary = { deliveries: [] };
    saveDeliveryRemarksInProgress = false;
    historicalCompletionDialogVisible = false;
    historicalCompletionDate: Date | null = null;
    historicalCompletionOverrideReason = '';
    historicalCompletionInProgress = false;
    payloadTypes: PayloadType[] = [];
    uomList: UOM[] = [];
    weightUnitList: Weight[] = [];
    deliveryContainerCases: ContainerCaseDeliveryContext[] = [];
    deliveryContainerCasesLoading = false;
    containerCaseDialogVisible = false;
    unassignedContainers: ShipmentContainerOption[] = [];
    selectedContainerIds: number[] = [];
    assigningContainers = false;
    deliveryFieldWorkPackages: FieldWorkPackage[] = [];
    deliveryFieldWorkLoading = false;
    fieldWorkCreateVisible = false;
    fieldWorkCreateSaving = false;
    fieldWorkCreateError = '';
    fieldWorkPackageDraft: FieldWorkPackage = {};
    deliveryFieldWorkLocationOptions: FieldWorkLocationOption[] = [];
    deliveryFinanceSummary?: WorkforceDeliveryFinanceSummary;
    deliveryPayTrip?: DriverPayTripView;
    deliveryPayTripBusy = false;
    deliveryPayTripError = '';
    quotedDriverRate?: number;
    driverRateMessage = '';
    deliveryFinanceLoading = false;
    tripMoneyDialogVisible = false;
    tripMoneySaving = false;
    tripMoneyError = '';
    tripMoneyDraft: DeliveryTripMoneyRequest = {};
    cashBoxOptions: DeliveryCashBoxOption[] = [];
    cashBoxError = '';
    availableCashBoxes(currencyCode?: string): DeliveryCashBoxOption[] {
        return this.cashBoxOptions.filter(box => box.currencyCode === currencyCode);
    }

	constructor(private router: Router,
		private route: ActivatedRoute,
		private dialogService: DialogService,
		private shipmentService: ShipmentService,
		private containerTrackingService: ContainerTrackingService,
		private messageService: MessageService,
        private driverService: DriverService,
        private vehicleService: VehicleService,
		private sessionStorageService: SessionStorageService,
        public permissionService: PermissionService,
        private pettyCash: PettyCashService) {
        this.payloadTypes = this.sessionStorageService.getPayloadTypes() ?? [];
        this.uomList = this.sessionStorageService.getUOMList() ?? [];
        this.weightUnitList = this.sessionStorageService.getWeightUnits() ?? [];
	}

    /** Receives the read-only tracking summary without coupling operational tabs to GPS polling. */
    onTrackingSummaryChange(summary: DeliveryTrackingSummary): void {
        this.liveTrackingDistance.set(summary.distance);
        this.liveTrackingEta.set(summary.eta);
        this.trackingStatus.set(summary.status);
        if (summary.delayed && !this.alerts().includes('Delivery is experiencing delays')) {
            this.alerts.update(alerts => [...alerts, 'Delivery is experiencing delays']);
            this.hasAlerts.set(true);
        }
    }

    trackingStatusLabel(): string {
        switch (this.trackingStatus()) {
            case 'ACTIVE_LOCATION': return 'Active';
            case 'STALE_LOCATION': return 'Stale';
            case 'NO_GPS_MAPPING': return 'No mapping';
            case 'NO_LOCATION': return 'No location';
            default: return this.trackingStatus() || 'Unavailable';
        }
    }

    trackingStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const status = this.trackingStatus();
        return status === 'ACTIVE_LOCATION' ? 'success'
            : status === 'STALE_LOCATION' ? 'warn'
            : status === 'NO_GPS_MAPPING' || status === 'NO_LOCATION' ? 'danger'
            : 'secondary';
    }

    private companyId(): number | undefined {
        const company = this.sessionStorageService.getCompany() ?? this.sessionStorageService.getCustomer()?.company;
        return company?.id ? Number(company.id) : undefined;
    }

	ngOnInit(): void {
		this.route.params.subscribe(params => {
			this.deliveryId = Number(params['deliveryId']);
            const requestedDeliveryId = this.deliveryId;
            const requestToken = ++this.deliveryLoadRequestToken;
            this.isLoading = true;
            this.deliveryNotesLoading = false;
            this.deliveryWaybillsLoading = false;
            this.deliveryContainerCasesLoading = false;
            this.deliveryFieldWorkLoading = false;
            this.deliveryFinanceLoading = false;
            this.deliveryNotesLoaded = false;
            this.deliveryFinanceRequested = false;
            this.deliveryNotesError = false;
            this.deliveryWaybillsError = false;
            this.deliveryContainerCasesError = false;
            this.deliveryFieldWorkError = false;
            this.deliveryFinanceError = false;
            this.deliveryRemarksHtml = '';
            this.deliveryRemarksFeed = { entries: [] };
            this.deliveryWaybillSummary = { deliveries: [] };
            this.deliveryContainerCases = [];
            this.unassignedContainers = [];
            this.selectedContainerIds = [];
            this.deliveryFieldWorkPackages = [];
            this.deliveryFinanceSummary = undefined;
            this.shipmentService.getShipmentAndDeliveryByDeliveryId(requestedDeliveryId).subscribe({
                next: (shipment: Shipment) => {
                    if (requestToken !== this.deliveryLoadRequestToken) {
                        return;
                    }
                    const delivery = shipment?.deliveries?.find(item => item.deliveryId === requestedDeliveryId);
                    if (!shipment || !delivery) {
                        this.handleDeliveryLoadFailure();
                        return;
                    }
                    this.initializeDeliveryView(shipment, delivery);
                },
                error: () => {
                    if (requestToken === this.deliveryLoadRequestToken) {
                        this.handleDeliveryLoadFailure();
                    }
                }
            });
		});
        this.route.queryParamMap.subscribe(queryParams => {
            this.requestedDeliveryTabKey = queryParams.get('tab');
            this.activeTabIndex = this.resolveDeliveryTabValue(this.requestedDeliveryTabKey);
            this.loadActiveDeliveryTabData();
        });
	}

    private initializeDeliveryView(shipment: Shipment, delivery: ShipmentDelivery): void {
        this.shipment = shipment;
        this.shipmentDelivery = delivery;
        this.shipment.payloadItemList = this.shipment?.payloadItemList ?? [];
        this.shipmentDelivery.payloadItemList = this.shipmentDelivery?.payloadItemList ?? [];
        this.shipmentDeliveryAttachments = this.shipmentDelivery?.filesList ?? [];
        this.shipmentInboundRequests = [];
        if (shipment.warehouseFlowDirection === 'INBOUND' && shipment.shipmentId) {
            const requestToken = this.deliveryLoadRequestToken;
            this.warehousingService.getShipmentInbounds(shipment.shipmentId).subscribe({
                next: response => {
                    if (requestToken !== this.deliveryLoadRequestToken) return;
                    this.shipmentInboundRequests = (response.data || []).map(request => ({
                        ...request, lines: request.lines.filter(line => line.deliveryId === delivery.deliveryId)
                    })).filter(request => request.lines.length > 0);
                }
            });
        }
        this.deliveryFilesCount = this.shipmentDeliveryAttachments.length;
        this.isLoading = false;

        this.breadcrumbItems = [{ label: 'Shipments', routerLink: '/shipment/company' },
        { label: this.shipment?.shipmentNumber, routerLink: ['/shipment/view', this.shipment?.shipmentId] },
        { label: this.shipmentDelivery?.deliveryNumber }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/dashboard' };

        this.loadDeliveryActionData();
        this.loadOperationStatus();
        this.loadDeliveryWaybills();
        this.loadDeliveryContainerCases();
        this.loadUnassignedContainers();
        this.loadDeliveryFieldWork();
        this.loadActiveDeliveryTabData();
    }

    private handleDeliveryLoadFailure(): void {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load delivery details.' });
        this.router.navigate(['/shipment/company']);
    }

    backToShipment(): void {
        if (this.shipment?.shipmentId) {
            this.router.navigate(['/shipment/view', this.shipment.shipmentId, 'advanced']);
        }
    }

    canCreateContainerCase(): boolean {
        if (this.isCancelled()) {
            return false;
        }
        return !!this.shipmentDelivery?.deliveryId
            && this.permissionService.hasPermission(app_permissions.container_tracking.cases.create);
    }

    private loadUnassignedContainers(): void {
        const shipmentId = this.shipment?.shipmentId;
        if (!shipmentId || !this.permissionService.hasPermission(app_permissions.shipments.deliveries.edit)
            || !this.permissionService.hasPermission(app_permissions.shipments.view)) return;
        const requestToken = this.deliveryLoadRequestToken;
        this.shipmentService.getShipmentContainers(shipmentId).subscribe({
            next: containers => {
                if (requestToken !== this.deliveryLoadRequestToken) return;
                this.unassignedContainers = containers.filter(container => !container.deliveryId)
                    .map(container => ({ ...container, containerNumber: container.containerNumber || container.caseNumber }));
            },
            error: () => {
                if (requestToken === this.deliveryLoadRequestToken)
                    this.messageService.add({ severity: 'error', summary: 'Containers unavailable', detail: 'Shipment containers could not be loaded.' });
            }
        });
    }

    assignContainers(): void {
        const shipmentId = this.shipment?.shipmentId;
        if (!shipmentId || !this.deliveryId || !this.selectedContainerIds.length) return;
        this.assigningContainers = true;
        this.shipmentService.assignShipmentContainers(shipmentId, this.deliveryId, this.selectedContainerIds)
            .pipe(finalize(() => this.assigningContainers = false)).subscribe({
                next: () => {
                    this.selectedContainerIds = [];
                    this.loadUnassignedContainers();
                    this.loadDeliveryContainerCases();
                    this.messageService.add({ severity: 'success', summary: 'Containers assigned', detail: 'Containers were linked to this delivery.' });
                },
                error: error => this.messageService.add({ severity: 'error', summary: 'Assignment failed', detail: error?.error?.message || 'Containers could not be assigned.' })
            });
    }

    canViewContainerCases(): boolean {
        return this.permissionService.hasPermission(app_permissions.container_tracking.cases.view);
    }

    hasDeliveryContainerCases(): boolean {
        return this.canViewContainerCases() && this.deliveryContainerCases.length > 0;
    }

    private loadDeliveryContainerCases(): void {
        if (!this.deliveryId || !this.canViewContainerCases()) {
            this.deliveryContainerCases = [];
            return;
        }
        const deliveryId = this.deliveryId;
        this.deliveryContainerCasesLoading = true;
        this.deliveryContainerCasesError = false;
        this.containerTrackingService.findCasesForShipmentDelivery(deliveryId)
            .subscribe({
                next: rows => {
                    if (deliveryId !== this.deliveryId) return;
                    this.deliveryContainerCasesLoading = false;
                    this.deliveryContainerCases = rows || [];
                    this.resetContainerTabIfUnavailable();
                },
                error: () => {
                    if (deliveryId !== this.deliveryId) return;
                    this.deliveryContainerCasesLoading = false;
                    this.deliveryContainerCases = [];
                    this.deliveryContainerCasesError = true;
                    this.resetContainerTabIfUnavailable();
                }
            });
    }

    retryDeliveryContainerCases(): void {
        this.loadDeliveryContainerCases();
    }

    private resetContainerTabIfUnavailable(): void {
        if (this.deliveryContainerCases.length === 0 && !this.deliveryContainerCasesError && this.activeTabIndex === 10) {
            this.activeTabIndex = 0;
            this.updateDeliveryTabQueryParam();
        }
    }

    canViewFieldWork(): boolean {
        return this.permissionService.hasPermission(app_permissions.operations.fieldWork.view);
    }

    canManageFieldWork(): boolean {
        if (this.isCancelled()) {
            return false;
        }
        return this.permissionService.hasPermission(app_permissions.operations.fieldWork.manage);
    }

    hasDeliveryFieldWork(): boolean {
        return this.canViewFieldWork() && this.deliveryFieldWorkPackages.length > 0;
    }

    openDeliveryFieldWork(): void {
        if (this.hasDeliveryFieldWork()) {
            this.activeTabIndex = 11;
            this.updateDeliveryTabQueryParam();
            return;
        }
        if (this.isCancelled() || !this.canManageFieldWork()) {
            return;
        }

        const start = this.validDate(this.shipment?.pickupTime) ?? new Date();
        const requestedEnd = this.validDate(this.shipment?.dropoffTime);
        const end = requestedEnd && requestedEnd.getTime() > start.getTime()
            ? requestedEnd
            : new Date(start.getTime() + 8 * 60 * 60 * 1000);
        this.fieldWorkPackageDraft = {
            title: `${this.shipmentDelivery?.deliveryNumber || 'Delivery'} field work`,
            activityType: 'DELIVERY',
            targetType: 'SHIPMENT_DELIVERY',
            targetId: this.shipmentDelivery?.deliveryId,
            targetReference: this.shipmentDelivery?.deliveryNumber,
            logisticsProjectId: this.shipmentDelivery?.logisticsProjectId,
            customerCrmClientId: this.shipment?.crmClientId,
            locationId: this.shipment?.destinationLocation?.id,
            locationName: this.shipment?.destinationLocation?.name,
            plannedStart: start,
            plannedEnd: end,
            billable: false,
            status: 'DRAFT'
        };
        this.fieldWorkCreateError = '';
        this.fieldWorkCreateVisible = true;
    }

    createDeliveryFieldWork(value: FieldWorkPackage): void {
        if (this.isCancelled() || this.fieldWorkCreateSaving) {
            return;
        }
        this.fieldWorkCreateSaving = true;
        this.fieldWorkCreateError = '';
        this.fieldWorkService.savePackage(value)
            .pipe(finalize(() => this.fieldWorkCreateSaving = false))
            .subscribe({
                next: created => {
                    this.deliveryFieldWorkPackages = [created];
                    this.fieldWorkCreateVisible = false;
                    this.activeTabIndex = 11;
                    this.updateDeliveryTabQueryParam();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Field work added',
                        detail: `${created.workPackageNumber || 'The work package'} is ready for planning.`
                    });
                },
                error: error => {
                    this.fieldWorkCreateError = error?.error?.message || error?.error?.error
                        || error?.message || 'Failed to create field work.';
                }
            });
    }

    private loadDeliveryFieldWork(): void {
        if (!this.deliveryId || !this.canViewFieldWork()) {
            this.deliveryFieldWorkPackages = [];
            this.resetFieldWorkTabIfUnavailable();
            return;
        }
        const deliveryId = this.deliveryId;
        this.deliveryFieldWorkLoading = true;
        this.deliveryFieldWorkError = false;
        this.fieldWorkService.packages({ targetType: 'SHIPMENT_DELIVERY', targetId: deliveryId })
            .subscribe({
                next: packages => {
                    if (deliveryId !== this.deliveryId) return;
                    this.deliveryFieldWorkLoading = false;
                    this.deliveryFieldWorkPackages = packages || [];
                    if (this.deliveryFieldWorkPackages.length > 0
                        && this.requestedDeliveryTabKey?.toLowerCase() === 'field-work') {
                        this.activeTabIndex = 11;
                    }
                    this.resetFieldWorkTabIfUnavailable();
                },
                error: () => {
                    if (deliveryId !== this.deliveryId) return;
                    this.deliveryFieldWorkLoading = false;
                    this.deliveryFieldWorkPackages = [];
                    this.deliveryFieldWorkError = true;
                    this.resetFieldWorkTabIfUnavailable();
                }
            });
    }

    retryDeliveryFieldWork(): void {
        this.loadDeliveryFieldWork();
    }

    private resetFieldWorkTabIfUnavailable(): void {
        if (this.deliveryFieldWorkPackages.length === 0 && !this.deliveryFieldWorkError && this.activeTabIndex === 11) {
            this.activeTabIndex = 0;
            this.updateDeliveryTabQueryParam();
        }
    }

    private loadDeliveryFinance(): void {
        const companyId = this.companyId();
        if (!companyId || !this.deliveryId
            || !this.permissionService.hasPermission(app_permissions.accounts.workforce_finance.view)) {
            this.deliveryFinanceSummary = undefined;
            this.deliveryPayTrip = undefined;
            return;
        }
        const deliveryId = this.deliveryId;
        this.loadDeliveryPayTrip(companyId, deliveryId);
        this.deliveryFinanceLoading = true;
        this.deliveryFinanceError = false;
        this.deliveryFinanceRequested = true;
        this.workforceFinance.deliveryFinance(companyId, deliveryId)
            .subscribe({
                next: summary => {
                    if (deliveryId !== this.deliveryId) return;
                    this.deliveryFinanceLoading = false;
                    this.deliveryFinanceSummary = summary;
                },
                error: () => {
                    if (deliveryId !== this.deliveryId) return;
                    this.deliveryFinanceLoading = false;
                    this.deliveryFinanceSummary = undefined;
                    this.deliveryFinanceError = true;
                }
            });
    }

    private loadDeliveryPayTrip(companyId: number, deliveryId: number): void {
        const shipmentId = this.shipment?.shipmentId || this.shipmentDelivery?.shipmentId;
        if (!shipmentId) return;
        this.workforceFinance.driverPayTrips(companyId, shipmentId).subscribe({
            next: views => {
                if (deliveryId === this.deliveryId)
                    this.deliveryPayTrip = views.find(view => view.deliveries.some(link => link.deliveryId === deliveryId));
            },
            error: () => { if (deliveryId === this.deliveryId) this.deliveryPayTrip = undefined; }
        });
    }

    deliveryPayTripAllocation(): number | undefined {
        return this.deliveryPayTrip?.deliveries.find(link => link.deliveryId === this.deliveryId)?.allocatedPayAmount;
    }

    captureDeliveryPayTrip(boundary: 'START' | 'END', event?: Event): void {
        const trip = this.deliveryPayTrip?.trip;
        const companyId = this.companyId();
        if (!trip || !companyId || this.deliveryPayTripBusy) return;
        const file = (event?.target as HTMLInputElement | undefined)?.files?.[0];
        if (trip.intendedDistanceSource === 'PHOTO_ODOMETER' && !file) {
            this.deliveryPayTripError = 'Choose a dashboard odometer photo.'; return;
        }
        if (file && (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 9_000_000)) {
            this.deliveryPayTripError = 'Use a JPEG, PNG, or WebP image below 9 MB.'; return;
        }
        const send = (imageBase64?: string) => {
            this.deliveryPayTripBusy = true; this.deliveryPayTripError = '';
            this.workforceFinance.captureDriverPayTrip(trip.driverPayTripId, companyId, boundary, {
                imageBase64, mimeType: file?.type,
                place: boundary === 'START' ? trip.expectedStartPlace || 'Before pickup'
                    : trip.expectedParkingPlace || 'Approved parking'
            }).pipe(finalize(() => this.deliveryPayTripBusy = false)).subscribe({
                next: () => this.loadDeliveryPayTrip(companyId, this.deliveryId),
                error: error => this.deliveryPayTripError = error?.error?.message || 'Pay trip evidence could not be saved.'
            });
        };
        if (!file) { send(); return; }
        const reader = new FileReader();
        reader.onload = () => send(String(reader.result).split(',')[1]);
        reader.onerror = () => this.deliveryPayTripError = 'The odometer photo could not be read.';
        reader.readAsDataURL(file);
    }

    retryDeliveryFinance(): void {
        this.loadDeliveryFinance();
    }

    canManageDeliveryTripMoney(): boolean {
        if (this.isCancelled()) {
            return false;
        }
        return this.permissionService.hasPermission(app_permissions.accounts.workforce_finance.earnings_manage)
            && this.permissionService.hasPermission(app_permissions.accounts.workforce_finance.cash_manage)
            && this.deliveryFinanceSummary?.canManageTripMoney !== false;
    }

    openTripMoneyDialog(): void {
        if (this.isCancelled() || !this.canManageDeliveryTripMoney()) return;
        this.tripMoneyError = '';
        this.cashBoxError = '';
        this.driverRateMessage = '';
        const existingCash = this.deliveryFinanceSummary?.cashRequests?.find(row => row.status !== 'CANCELLED');
        this.tripMoneyDraft = {
            enabled: true,
            applyRateCardDefault: true,
            payMethod: this.shipmentDelivery?.driverPayMethod || (this.deliveryPayTrip ? 'PER_KM'
                : this.deliveryFinanceSummary?.tripEarnings?.length ? 'FIXED_TRIP' : 'NONE'),
            ratePerKm: this.deliveryPayTrip?.trip.ratePerKm,
            distanceSource: (this.deliveryPayTrip?.trip.intendedDistanceSource as DeliveryTripMoneyRequest['distanceSource']) || 'PHOTO_ODOMETER',
            expectedStartPlace: this.deliveryPayTrip?.trip.expectedStartPlace,
            expectedParkingPlace: this.deliveryPayTrip?.trip.expectedParkingPlace,
            payTripGroupKey: this.deliveryPayTrip?.trip.groupKey,
            cashPurpose: existingCash?.purpose as DeliveryTripMoneyRequest['cashPurpose'] || 'NONE',
            pettyCashBoxId: existingCash?.pettyCashBoxId,
            currencyCode: this.deliveryFinanceSummary?.currencyCode || this.shipment?.currencyCode || this.currencyCode() || 'SAR',
            tripEarningAmount: null,
            tripCashAmount: null,
            reason: ''
        };
        this.tripMoneyDialogVisible = true;
        this.loadDriverRateQuote();
        const companyId = this.companyId();
        if (companyId) this.pettyCash.deliveryBoxOptions(companyId).subscribe({
            next: boxes => {
                this.cashBoxOptions = boxes;
                if (!this.tripMoneyDraft.pettyCashBoxId)
                    this.tripMoneyDraft.pettyCashBoxId = preferredDeliveryCashBoxId(boxes,
                        this.shipment?.sourceLocation?.id, this.tripMoneyDraft.currencyCode);
            },
            error: () => this.cashBoxError = 'Cash boxes could not be loaded. Reopen this form before requesting cash.'
        });
    }

    loadDriverRateQuote(): void {
        const companyId = this.companyId();
        const shipmentId = this.shipment?.shipmentId;
        const driverId = this.shipmentDelivery?.deliveryDriver?.id;
        const method = this.tripMoneyDraft.payMethod;
        if (method !== 'FIXED_TRIP' && method !== 'CITY_PAIR_TRIP')
            this.tripMoneyDraft.tripEarningAmount = null;
        this.quotedDriverRate = undefined;
        this.driverRateMessage = '';
        if (!companyId || !shipmentId || !driverId || !method || method === 'NONE') return;
        this.workforceFinance.driverRateQuote(companyId, shipmentId, driverId, method,
            this.tripMoneyDraft.currencyCode || this.shipment?.currencyCode || 'SAR').subscribe({
            next: quote => {
                if (this.tripMoneyDraft.payMethod !== method) return;
                this.quotedDriverRate = quote.payRate;
                if (method === 'PER_KM' && this.tripMoneyDraft.ratePerKm == null)
                    this.tripMoneyDraft.ratePerKm = quote.payRate;
            },
            error: error => this.driverRateMessage = error?.error?.message || 'No matching driver rate card. Finance must resolve this before Accounts posting.'
        });
    }

    saveTripMoney(): void {
        const companyId = this.companyId();
        if (this.isCancelled() || !companyId || !this.deliveryId || this.tripMoneySaving) return;
        const earningOverride = this.tripMoneyDraft.tripEarningAmount != null;
        const cashOverride = this.tripMoneyDraft.cashPurpose !== 'NONE' && this.tripMoneyDraft.tripCashAmount != null;
        const rateOverride = this.tripMoneyDraft.payMethod === 'PER_KM' && this.tripMoneyDraft.ratePerKm != null
            && this.tripMoneyDraft.ratePerKm !== (this.deliveryPayTrip?.trip.ratePerKm ?? this.quotedDriverRate);
        if ((earningOverride || cashOverride || rateOverride) && !this.tripMoneyDraft.reason?.trim()) {
            this.tripMoneyError = 'A reason is required when you override a driver rate or cash amount.';
            return;
        }
        if (this.tripMoneyDraft.enabled !== false && this.tripMoneyDraft.payMethod === 'PER_KM'
            && !this.tripMoneyDraft.distanceSource) {
            this.tripMoneyError = 'Choose the distance source for per-km pay.';
            return;
        }
        if (this.tripMoneyDraft.cashPurpose !== 'NONE' && !this.availableCashBoxes(this.tripMoneyDraft.currencyCode)
            .some(box => box.pettyCashBoxId === this.tripMoneyDraft.pettyCashBoxId)) {
            this.tripMoneyError = 'Choose an active custodian cash box for this driver cash request.';
            return;
        }
        this.tripMoneySaving = true;
        this.tripMoneyError = '';
        this.workforceFinance.saveDeliveryTripMoney(companyId, this.deliveryId, {
            ...this.tripMoneyDraft,
            createdBy: this.sessionStorageService.getCustomer()?.id,
            reason: this.tripMoneyDraft.reason?.trim() || undefined
        }).pipe(finalize(() => this.tripMoneySaving = false)).subscribe({
            next: summary => {
                this.deliveryFinanceSummary = summary;
                if (this.shipmentDelivery)
                    this.shipmentDelivery.driverPayMethod = this.tripMoneyDraft.enabled === false
                        ? 'NONE' : this.tripMoneyDraft.payMethod;
                this.loadDeliveryPayTrip(companyId, this.deliveryId!);
                this.tripMoneyDialogVisible = false;
                this.messageService.add({ severity: 'success', summary: 'Trip money saved', detail: 'The delivery finance proposal was updated.' });
            },
            error: error => this.tripMoneyError = error?.error?.message || error?.error?.error
                || error?.message || 'Trip money could not be saved.'
        });
    }

    openWorkforceFinance(tab: string): void {
        this.router.navigate(['/accounts/enterprise/workforce-finance'], {
            queryParams: { tab, deliveryId: this.deliveryId }
        });
    }

    handlingEarningTotal(): number {
        return (this.deliveryFinanceSummary?.handlingEarnings || [])
            .reduce((total, line) => total + (line.amount || 0), 0);
    }

    /**
     * Cash allowances are accountable operating cash, not driver earnings. The
     * finance endpoint deliberately separates trip-money proposals from other
     * delivery allowances; the delivery view needs both collections.
     */
    deliveryCashAdvances(): WorkforceCashAdvance[] {
        const advances = [
            ...(this.deliveryFinanceSummary?.tripCashAdvances || []),
            ...(this.deliveryFinanceSummary?.otherCashAdvances || [])
        ];
        const seen = new Set<number | string>();
        return advances.filter((advance, index) => {
            const key = advance.workforceCashAdvanceId ?? advance.advanceNumber ?? `row-${index}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    driverAllowanceRequestedTotal(): number {
        return this.deliveryCashAdvances()
            .reduce((total, advance) => total + (advance.requestedAmount || 0), 0);
    }

    driverAllowanceIssuedTotal(): number {
        return this.deliveryCashAdvances()
            .reduce((total, advance) => total + (advance.issuedAmount || 0), 0);
    }

    driverAllowanceSource(advance: WorkforceCashAdvance): string {
        if (advance.originCode === 'DELIVERY_TRIP_MONEY') return 'Trip money';
        return advance.originCode
            ? advance.originCode.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
            : 'Operational allowance';
    }

    private validDate(value?: Date | string | number): Date | undefined {
        if (!value) return undefined;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }

    deliveryContainerLeg(context: ContainerCaseDeliveryContext): ContainerTransportLeg | undefined {
        return context.deliveryTransportLegs?.[0];
    }

    containerCaseRoute(context: ContainerCaseDeliveryContext): any[] {
        return ['/container-tracking/cases/view', context.containerCase?.id];
    }

    openCreateContainerCaseDialog(): void {
        if (!this.canCreateContainerCase()) {
            return;
        }
        this.containerCaseDialogVisible = true;
    }

    onContainerCaseCreated(result: ContainerCaseFromShipmentDeliveryResult): void {
        const createdCase = result.containerCase;
        this.messageService.add({
            severity: 'success',
            summary: 'Container case created',
            detail: `The journey plan and its first transport step are linked to delivery ${this.shipmentDelivery?.deliveryNumber || ''}.`
        });
        if (createdCase?.id) {
            this.router.navigate(
                ['/container-tracking/cases/view', createdCase.id],
                { queryParams: { tab: 'journey-plan' } }
            );
        }
    }

	showDate(longDate: number | undefined) {
		return longDate != undefined ? formatDate(new Date(longDate), 'dd-MM-yyyy HH:mm', 'en-US') : undefined;
	}

    getLocationShortAddress(address?: GeoAddress): string {
        if (!address) {
            return '';
        }
        return new GeoAddress().getGeoAddressFromObject(address).getShortAddress();
    }

	deliveryMetrics(): PageMetric[] {
        const metrics: PageMetric[] = [];
        if (this.canViewShipmentPricing()) {
            metrics.push({
                label: this.localeService.translate('pages.deliveryDetail.metrics.transportCharge'),
                value: this.formatAmount(this.shipment?.countryOfOrder?.currencyCode, this.shipment?.sellingPrice),
                hint: this.localeService.translate('pages.deliveryDetail.metrics.transportChargeHint'),
                tone: 'success'
            });
            metrics.push({
                label: this.localeService.translate('pages.deliveryDetail.metrics.chargeLines'),
                value: this.shipmentDelivery?.chargeLines?.length ?? 0,
                hint: this.localeService.translate('pages.deliveryDetail.metrics.chargeLinesHint'),
                tone: 'info'
            });
        }
        metrics.push(
            {
                label: this.localeService.translate('pages.deliveryDetail.metrics.files'),
                value: this.deliveryFilesCount,
                hint: this.localeService.translate('pages.deliveryDetail.metrics.filesHint'),
                tone: 'default'
            },
            {
                label: this.localeService.translate('pages.deliveryDetail.metrics.deliveryStatus'),
                value: this.currentWorkflowState(),
                hint: this.currentPhase(),
                tone: this.currentWorkflowState() === 'DELIVERY_COMPLETE' || this.currentWorkflowState() === 'CUSTOMER_ACKNOWLEDGED' ? 'success' : 'warn'
            }
        );
        return metrics;
    }

    canViewShipmentPricing(): boolean {
        return this.permissionService.hasPermission(app_permissions.shipments.pricing.view);
    }

    formatAmount(currency?: string, value?: number): string {
        const prefix = currency ? `${currency} ` : '';
        return `${prefix}${(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    currentWorkflowState(): string {
        return this.operationStatus?.deliveryStatus || this.shipmentDelivery?.workflowState || 'PLANNED';
    }

    shipmentWorkflowState(): string {
        return this.operationStatus?.shipmentStatus || this.shipment?.workflowState || String(this.shipment?.shipmentStatus || 'DRAFT');
    }

    isShipmentCancelled(): boolean {
        const state = String(this.shipmentWorkflowState() || this.shipment?.workflowState || this.shipment?.shipmentStatus || '').toUpperCase();
        return state === 'CANCELLED';
    }

    isDeliveryCancelled(): boolean {
        const deliveryState = String(this.currentWorkflowState() || this.shipmentDelivery?.workflowState || '').toUpperCase();
        const jobStatus = String(this.shipmentDelivery?.jobStatus || '').toUpperCase();
        return deliveryState === 'CANCELLED'
            || deliveryState === 'REJECTED'
            || jobStatus === 'JOB_DRIVER_CANCELLED'
            || jobStatus === 'JOB_DRIVER_REJECTED';
    }

    isCancelled(): boolean {
        return this.isShipmentCancelled() || this.isDeliveryCancelled();
    }

    currentPhase(): string {
        return this.operationStatus?.currentPhase || this.currentWorkflowState();
    }

    orderReleaseStatusLabel(): string {
        const releases = this.operationStatus?.orderReleases ?? [];
        if (!releases.length) return 'Not linked';
        const uniqueStatuses = Array.from(new Set(releases.map(release => release.releaseStatus || 'PENDING')));
        return uniqueStatuses.join(', ');
    }

    shipmentNumber(): string {
        return this.shipment?.shipmentNumber || '';
    }

    deliveryAuditStamp(): AuditStamp {
        return {
            showWhenEmpty: true,
            createdAt: this.shipmentDelivery?.createdAt,
            createdBy: this.shipmentDelivery?.createdBy,
            createdByActor: this.shipmentDelivery?.createdByActor,
            updatedAt: this.shipmentDelivery?.updatedAt,
            updatedBy: this.shipmentDelivery?.updatedBy,
            updatedByActor: this.shipmentDelivery?.updatedByActor,
        };
    }

    deliveryPayloadItems(): PayloadItemView[] {
        return this.shipmentDelivery?.payloadItemList?.length
            ? this.shipmentDelivery.payloadItemList
            : (this.shipment?.payloadItemList ?? []);
    }

    payloadCount(): number {
        return this.deliveryPayloadItems().length;
    }

    payloadDescription(item?: PayloadItemView): string {
        return item?.warehouseItemName
            || item?.itemDescription
            || item?.itemDesciption
            || item?.itemType?.name
            || 'Payload';
    }

    payloadQuantityLabel(item?: PayloadItemView): string {
        const quantity = item?.itemQuantity?.quantity;
        const unit = item?.itemQuantity?.uom?.name || '';
        return quantity ? `${quantity} ${unit}`.trim() : '-';
    }

    payloadTotalWeightLabel(item?: PayloadItemView): string {
        const totalWeight = Number(item?.itemWeight ?? 0) * Number(item?.itemQuantity?.quantity ?? 0);
        const unit = item?.weightUnit?.shortName || '';
        return totalWeight > 0 ? `${Number(totalWeight.toFixed(3))} ${unit}`.trim() : 'Weight pending';
    }

    payloadSkuLabel(item?: PayloadItemView): string {
        return [item?.warehouseItemNumber, item?.skuNumber, item?.customerItemNumber]
            .filter(Boolean)
            .join(' / ') || '-';
    }

    payloadItemSizeLabel(item?: PayloadItemView): string {
        return item?.itemSize || '-';
    }

    payloadSourceLabel(item?: PayloadItemView): string {
        const parts = [
            item?.sourceWarehouseLabel,
            item?.sourceZoneLabel,
            item?.conditionLabel
        ].filter(Boolean);
        return parts.join(' / ') || '-';
    }

    payloadMovementLabel(item?: PayloadItemView): string {
        const parts = [
            item?.linkedWarehouseTransferNumber,
            item?.linkedWarehouseDispatchNumber
        ].filter(Boolean);
        return parts.join(' / ') || '-';
    }

    hasLogisticsProject(): boolean {
        return this.includedLogisticsProjects().length > 0;
    }

    hasHistoricalWaybill(): boolean {
        if ((this.shipmentDeliveryAttachments ?? []).length > 0) {
            return this.shipmentDeliveryAttachments.some(file => isHistoricalWaybillAttachment(file));
        }
        return !!this.shipmentDelivery?.isPhysicalWaybillReceived;
    }

    latestHistoricalWaybill(): ShipmentDeliveryAttachment | undefined {
        return latestHistoricalWaybillAttachment(this.shipmentDeliveryAttachments);
    }

    canMarkHistoricalCompletion(): boolean {
        if (this.isCancelled()) {
            return false;
        }
        const state = this.currentWorkflowState();
        return this.hasHistoricalWaybill()
            && state !== 'DELIVERY_COMPLETE'
            && state !== 'CUSTOMER_ACKNOWLEDGED'
            && state !== 'CANCELLED';
    }

    logisticsProjectId(): number | undefined {
        return this.primaryLogisticsProjectId();
    }

    logisticsProjectRoute(): any[] {
        return ['/logistics-projects', this.logisticsProjectId()];
    }

    hasMultipleLogisticsProjects(): boolean {
        return this.includedLogisticsProjects().length > 1;
    }

    logisticsProjectTagLabel(): string {
        const projects = this.includedLogisticsProjects();
        if (projects.length > 1) {
            return `${projects.length} Projects`;
        }
        const project = projects[0];
        return project?.projectNumber || (project?.projectId ? `Project #${project.projectId}` : 'Project');
    }

    logisticsProjectMenuItems(): MenuItem[] {
        return this.includedLogisticsProjects().map(project => ({
            label: this.logisticsProjectLabel(project),
            icon: 'fa-solid fa-briefcase',
            command: () => this.openLogisticsProject(project.projectId)
        }));
    }

    openPrimaryLogisticsProject(): void {
        this.openLogisticsProject(this.primaryLogisticsProjectId());
    }

    private openLogisticsProject(projectId?: number): void {
        if (!projectId) {
            return;
        }
        this.router.navigate(['/logistics-projects', projectId]);
    }

    private primaryLogisticsProjectId(): number | undefined {
        return this.includedLogisticsProjects()[0]?.projectId
            || this.shipmentDelivery?.logisticsProjectId
            || this.shipment?.logisticsProjectId;
    }

    private includedLogisticsProjects(): DeliveryNoteLogisticsProject[] {
        const deliveryProjects = (this.shipmentDelivery?.includedLogisticsProjects ?? [])
            .filter(project => !!project?.projectId);
        if (deliveryProjects.length > 0) {
            return deliveryProjects;
        }
        const shipmentProjects = (this.shipment?.includedLogisticsProjects ?? [])
            .filter(project => !!project?.projectId);
        if (shipmentProjects.length > 0) {
            return shipmentProjects;
        }
        const projectId = this.shipmentDelivery?.logisticsProjectId || this.shipment?.logisticsProjectId;
        return projectId ? [{ projectId }] : [];
    }

    private logisticsProjectLabel(project?: DeliveryNoteLogisticsProject): string {
        const name = project?.projectName || project?.projectNumber || (project?.projectId ? `Project #${project.projectId}` : 'Project');
        return project?.createdByCustomerName ? `${name} - ${project.createdByCustomerName}` : name;
    }

    sourceLocationName(): string {
        return this.shipment?.sourceLocation?.name || 'Source location';
    }

    sourceLocationAddress(): string {
        return this.shipment?.pickupCity || this.getLocationShortAddress(this.shipment?.sourceLocation?.address) || '';
    }

    destinationLocationName(): string {
        return this.shipment?.destinationLocation?.name || 'Destination location';
    }

    destinationLocationAddress(): string {
        return this.shipment?.dropoffCity || this.getLocationShortAddress(this.shipment?.destinationLocation?.address) || '';
    }

    currencyCode(): string | undefined {
        return this.shipment?.countryOfOrder?.currencyCode;
    }

    routeDistanceLabel(): string {
        if (!this.shipment?.distance) return 'Not calculated';
        const distance = this.shipment.distance > 1000 ? this.shipment.distance / 1000 : this.shipment.distance;
        return `${distance.toLocaleString('en-US', { maximumFractionDigits: 1 })} km`;
    }

    routeDurationLabel(): string {
        if (!this.shipment?.time) return 'Not calculated';
        const totalMinutes = Math.round(this.shipment.time / 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours === 0) return `${minutes} min`;
        return `${hours}h ${minutes}m`;
    }

    timelineProgress(): number {
        return calculateDeliveryOperationProgress(
            this.shipmentDelivery,
            this.operationStatus ?? undefined,
            Boolean(this.shipmentDelivery?.deliveryDriver || this.shipmentDelivery?.deliveryVehicle)
        ).percent;
    }

    timelineState(stop: ShipmentDeliveryStop | ShipmentOperationStopStatus): 'done' | 'active' | 'pending' {
        if (this.isStopComplete(stop.workflowState)) return 'done';
        const active = 'active' in stop && stop.active;
        const next = 'next' in stop && stop.next;
        if (active || next || stop.workflowState && stop.workflowState !== 'PENDING') return 'active';
        return 'pending';
    }

    pickupProgressLabel(): string {
        return this.progressLabel('pickupProgress', 'Pickup');
    }

    dropoffProgressLabel(): string {
        return this.progressLabel('dropoffProgress', 'Dropoff');
    }

    private progressLabel(key: 'pickupProgress' | 'dropoffProgress', label: string): string {
        const progress = this.operationStatus?.[key];
        if (progress) {
            return `${label} ${progress.completed ?? 0}/${progress.total ?? 0} complete`;
        }
        const stops = this.deliveryStops.filter(stop => String(stop.stopType || '').toUpperCase() === label.toUpperCase());
        const completed = stops.filter(stop => this.isStopComplete(stop.workflowState)).length;
        return `${label} ${completed}/${stops.length} complete`;
    }

    activeOrNextStopLabel(): string {
        const stop = this.operationStatus?.activeStop || this.operationStatus?.nextStop;
        if (!stop) return 'No pending site';
        const prefix = stop.active ? 'Active' : 'Next';
        return `${prefix} ${this.displayState(stop.stopType)} #${stop.stopNumber || '-'}`;
    }

    isMovementStop(stopType?: string): boolean {
        const value = String(stopType || '').toUpperCase();
        return value === 'PICKUP' || value === 'DROPOFF';
    }

    isStopComplete(state?: string): boolean {
        const value = String(state || '').toUpperCase();
        return value === 'COMPLETE' || value === 'SKIPPED';
    }

    displayState(value?: string): string {
        if (!value) return 'Pending';
        const text = value.toLowerCase().replaceAll('_', ' ');
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    driverName(): string {
        return this.shipmentDelivery?.deliveryDriver?.legalName || this.shipmentDelivery?.deliveryDriver?.name || 'Unassigned';
    }

    driverImage(): string {
        return this.shipmentDelivery?.deliveryDriver?.imageUrl || 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    }

    driverPhone(): string {
        return this.shipmentDelivery?.deliveryDriver?.phoneNumber || '-';
    }

    driverNationality(): string {
        return this.shipmentDelivery?.deliveryDriver?.nationality?.name || '-';
    }

    driverResidenceNumber(): string {
        return this.shipmentDelivery?.deliveryDriver?.residenceNumber || '-';
    }

    driverResidenceExpiry(): string {
        return this.showDate(this.shipmentDelivery?.deliveryDriver?.residenceExpiryDate) || '-';
    }

    driverLicenseExpiry(): string {
        return this.showDate(this.shipmentDelivery?.deliveryDriver?.licenseExpiryDate) || '-';
    }

    driverResourceCompany(): string {
        return this.shipmentDelivery?.deliveryDriver?.resourceCompanyName || '-';
    }

    driverOwnershipType(): string {
        return fleetResourceOwnershipTypeLabel(this.shipmentDelivery?.deliveryDriver);
    }

    driverOwnerLabel(): string {
        return fleetResourceOwnerLabel(this.shipmentDelivery?.deliveryDriver);
    }

    driverAgencyLabel(): string {
        return fleetResourceAgencyLabel(this.shipmentDelivery?.deliveryDriver) || '-';
    }

    vehicleName(): string {
        const vehicle = this.shipmentDelivery?.deliveryVehicle;
        if (!vehicle) return 'Unassigned';
        return [vehicle.make?.name, vehicle.model?.name, vehicle.year?.year].filter(Boolean).join(' ') || vehicle.plateNumber || 'Vehicle';
    }

    vehicleTypeLabel(): string {
        const vehicle = this.shipmentDelivery?.deliveryVehicle;
        return [vehicle?.config?.name, vehicle?.capacity?.name].filter(Boolean).join(' ') || '-';
    }

    firstVehicleImage(): string | null {
        return this.shipmentDelivery?.deliveryVehicle?.vehicleImages?.[0] ?? null;
    }

    vehicleImage(): string {
        return this.shipmentDelivery?.deliveryVehicle?.year?.image
            || this.shipmentDelivery?.deliveryVehicle?.config?.image
            || 'https://cdn-icons-png.flaticon.com/512/870/870181.png';
    }

    vehiclePlateNumber(): string {
        return this.shipmentDelivery?.deliveryVehicle?.plateNumber || 'Unassigned';
    }

    vehicleEngineNumber(): string {
        return this.shipmentDelivery?.deliveryVehicle?.engineNumber || '-';
    }

    vehicleRegistrationNumber(): string {
        return this.shipmentDelivery?.deliveryVehicle?.registrationNumber || '-';
    }

    vehicleRegistrationExpiry(): string {
        return this.showDate(this.shipmentDelivery?.deliveryVehicle?.registrationExpiry) || '-';
    }

    vehicleResourceCompany(): string {
        return this.shipmentDelivery?.deliveryVehicle?.resourceCompanyName || '-';
    }

    vehicleOwnershipType(): string {
        return fleetResourceOwnershipTypeLabel(this.shipmentDelivery?.deliveryVehicle);
    }

    vehicleOwnerLabel(): string {
        return fleetResourceOwnerLabel(this.shipmentDelivery?.deliveryVehicle);
    }

    vehicleAgencyLabel(): string {
        return fleetResourceAgencyLabel(this.shipmentDelivery?.deliveryVehicle) || '-';
    }

    canManageDeliveryChanges(): boolean {
        if (this.isCancelled()) {
            return false;
        }
        return this.permissionService.hasPermission(app_permissions.shipments.deliveries.edit)
            || this.permissionService.hasPermission(app_permissions.shipments.deliveries.assign);
    }

    canReplaceDeliveryCrewAsset(): boolean {
        return !this.isCancelled() && this.canManageDeliveryChanges() && !this.isDeliveryTerminal();
    }

    canCancelCurrentDelivery(): boolean {
        return !this.isCancelled()
            && this.canManageDeliveryChanges()
            && !this.isDeliveryTerminal()
            && !this.hasDeliverySiteExecutionStarted();
    }

    deliveryCancellationBlockedByExecution(): boolean {
        return !this.isCancelled()
            && this.canManageDeliveryChanges()
            && !this.isDeliveryTerminal()
            && this.hasDeliverySiteExecutionStarted();
    }

    openDeliveryCancellationDialog(): void {
        if (this.isCancelled() || !this.canCancelCurrentDelivery()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Cancellation unavailable',
                detail: 'A delivery can only be cancelled before any site activity has started. Use Replace Driver / Vehicle with a reason instead.'
            });
            return;
        }
        this.deliveryCancellationReason = '';
        this.deliveryCancellationDialogVisible = true;
    }

    closeDeliveryCancellationDialog(): void {
        if (!this.deliveryCancellationInProgress) {
            this.deliveryCancellationDialogVisible = false;
        }
    }

    cancelCurrentDelivery(): void {
        if (this.isCancelled()) {
            return;
        }
        const shipmentId = this.shipment?.shipmentId;
        const deliveryId = this.shipmentDelivery?.deliveryId;
        const reason = this.deliveryCancellationReason?.trim();
        if (!shipmentId || !deliveryId) {
            this.messageService.add({ severity: 'warn', summary: 'Delivery unavailable', detail: 'Delivery details are not loaded yet.' });
            return;
        }
        if (!reason) {
            this.messageService.add({ severity: 'warn', summary: 'Reason required', detail: 'Enter the reason for cancelling this delivery.' });
            return;
        }

        this.deliveryCancellationInProgress = true;
        this.shipmentService.cancelDelivery(shipmentId, deliveryId, {
            reason,
            actorCustomerId: this.sessionStorageService.getCustomer()?.id
        }).pipe(finalize(() => this.deliveryCancellationInProgress = false)).subscribe({
            next: delivery => {
                this.shipmentDelivery = delivery;
                this.deliveryCancellationDialogVisible = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Delivery cancelled',
                    detail: 'The cancelled delivery is retained in the audit trail and no longer counts toward shipment fulfilment.'
                });
                this.refreshDeliverySnapshot();
                this.refreshWorkflowData();
            },
            error: error => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Cancellation failed',
                    detail: error?.error?.message || 'The delivery could not be cancelled. Please review its operational status and try again.'
                });
            }
        });
    }

    openCrewAssetDialog(): void {
        if (this.isCancelled() || !this.canReplaceDeliveryCrewAsset()) {
            this.messageService.add({ severity: 'warn', summary: 'Change unavailable', detail: 'This delivery is already closed or cancelled.' });
            return;
        }
        this.selectedReplacementDriver = this.shipmentDelivery?.deliveryDriver ?? null;
        this.selectedReplacementVehicle = this.shipmentDelivery?.deliveryVehicle ?? null;
		// A replacement must be explicitly priced again; do not silently reuse a
		// prior driver/vehicle cost.
        this.crewAssetTransportCostAmount = null;
        this.crewAssetNoteHtml = this.defaultCrewAssetNote();
        this.crewAssetDialogVisible = true;
        this.loadCrewAssetOptions();
    }

    closeCrewAssetDialog(): void {
        if (this.crewAssetSaveInProgress) {
            return;
        }
        this.crewAssetDialogVisible = false;
    }

    saveCrewAssetChange(): void {
        if (this.isCancelled() || !this.shipment?.shipmentId || !this.shipmentDelivery?.deliveryId) {
            this.messageService.add({ severity: 'warn', summary: 'Delivery unavailable', detail: 'Delivery details are not loaded yet.' });
            return;
        }
        if (!this.hasCrewAssetChange()) {
            this.messageService.add({ severity: 'warn', summary: 'No change selected', detail: 'Select a different driver or vehicle before saving.' });
            return;
        }
        if (!this.noteHasText(this.crewAssetNoteHtml)) {
            this.messageService.add({ severity: 'warn', summary: 'Note required', detail: 'Add a shipment note before changing the crew or asset.' });
            return;
        }
        if (this.crewAssetTransportCostAmount == null || this.crewAssetTransportCostAmount < 0) {
            this.messageService.add({ severity: 'warn', summary: 'Cost confirmation required', detail: 'Confirm the replacement delivery transport cost before saving.' });
            return;
        }

        this.crewAssetSaveInProgress = true;
        this.shipmentService.reassignDeliveryCrewAsset(this.shipment.shipmentId, this.shipmentDelivery.deliveryId, {
            driverId: this.selectedReplacementDriver?.id ?? null,
            driverResourceCompanyId: this.selectedReplacementDriver?.resourceCompanyId ?? null,
            vehicleId: this.selectedReplacementVehicle?.vehicleId ?? null,
            vehicleResourceCompanyId: this.selectedReplacementVehicle?.resourceCompanyId ?? null,
            transportCostAmount: this.crewAssetTransportCostAmount,
            transportCostCurrencyCode: this.transportCostCurrencyCode(),
            actorCustomerId: this.sessionStorageService.getCustomer()?.id,
            reason: this.crewAssetNoteHtml,
            noteHtml: this.crewAssetNoteHtml
        }).subscribe({
            next: updatedDelivery => {
                this.shipmentDelivery = updatedDelivery;
                this.regenerateWaybillAfterCrewAssetChange(updatedDelivery);
            },
            error: error => {
                this.crewAssetSaveInProgress = false;
                this.messageService.add({ severity: 'error', summary: 'Update failed', detail: error?.error?.message || 'Could not change the driver or vehicle.' });
            }
        });
    }

    private regenerateWaybillAfterCrewAssetChange(updatedDelivery: ShipmentDelivery): void {
        if (updatedDelivery?.isPhysicalWaybillReceived || this.hasHistoricalWaybill()) {
            this.crewAssetSaveInProgress = false;
            this.crewAssetDialogVisible = false;
            this.messageService.add({
                severity: 'success',
                summary: 'Crew updated',
                detail: 'Driver/vehicle changed. Historical waybill remains authoritative.'
            });
            this.refreshDeliverySnapshot();
            this.refreshWorkflowData();
            return;
        }
        this.shipmentService.regenerateDeliveryWaybills(this.shipment.shipmentId ?? 0, updatedDelivery.deliveryId ?? 0)
            .pipe(finalize(() => this.crewAssetSaveInProgress = false))
            .subscribe({
                next: () => {
                    this.crewAssetDialogVisible = false;
                    this.messageService.add({ severity: 'success', summary: 'Crew updated', detail: 'Driver/vehicle changed and waybill regenerated.' });
                    this.refreshDeliverySnapshot();
                    this.refreshWorkflowData();
                },
                error: () => {
                    this.messageService.add({ severity: 'warn', summary: 'Crew updated', detail: 'Driver/vehicle changed, but waybill regeneration failed.' });
                    this.refreshDeliverySnapshot();
                    this.refreshWorkflowData();
                }
            });
    }

    private loadCrewAssetOptions(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        const agencyId = this.sessionStorageService.getCompany()?.transportationAgencyId;
        const emptyDriverResponse = { items: [] as Driver[] };
        const emptyVehicleResponse = { items: [] as Vehicle[] };

        this.crewAssetOptionsLoading = true;
        const transporterCrmClientId = this.shipmentDelivery?.transporterCrmClientId;
        if (companyId && transporterCrmClientId) {
            forkJoin({
                drivers: this.driverService.getTransporterDrivers(0, 250, companyId, transporterCrmClientId)
                    .pipe(catchError(() => of(emptyDriverResponse))),
                vehicles: this.vehicleService.getTransporterVehicles(0, 250, companyId, transporterCrmClientId)
                    .pipe(catchError(() => of(emptyVehicleResponse)))
            }).pipe(finalize(() => this.crewAssetOptionsLoading = false))
                .subscribe(result => {
                    this.availableDrivers = this.uniqueDrivers([
                        this.shipmentDelivery?.deliveryDriver,
                        ...(result.drivers.items ?? [])
                    ]);
                    this.availableVehicles = this.uniqueVehicles([
                        this.shipmentDelivery?.deliveryVehicle,
                        ...(result.vehicles.items ?? [])
                    ]);
                });
            return;
        }
        forkJoin({
            ownedDrivers: agencyId
                ? this.driverService.findDriversByTransportationAgencyId(0, 250, agencyId).pipe(catchError(() => of(emptyDriverResponse)))
                : of(emptyDriverResponse),
            freelanceDrivers: companyId
                ? this.driverService.getFreelanceDriversByCompanyId(0, 250, companyId).pipe(catchError(() => of(emptyDriverResponse)))
                : of(emptyDriverResponse),
            rentalDrivers: companyId
                ? this.driverService.getRentalDriversByCompanyId(0, 250, companyId).pipe(catchError(() => of(emptyDriverResponse)))
                : of(emptyDriverResponse),
            ownedVehicles: agencyId
                ? this.vehicleService.findVehiclesByTransportationAgencyId(0, 250, agencyId).pipe(catchError(() => of(emptyVehicleResponse)))
                : of(emptyVehicleResponse),
            freelanceVehicles: companyId
                ? this.vehicleService.getFreelanceVehiclesByCompanyId(0, 250, companyId).pipe(catchError(() => of(emptyVehicleResponse)))
                : of(emptyVehicleResponse),
            rentalVehicles: companyId
                ? this.vehicleService.getRentalVehiclesByCompanyId(0, 250, companyId).pipe(catchError(() => of(emptyVehicleResponse)))
                : of(emptyVehicleResponse)
        }).pipe(finalize(() => this.crewAssetOptionsLoading = false))
            .subscribe(result => {
                this.availableDrivers = this.uniqueDrivers([
                    this.shipmentDelivery?.deliveryDriver,
                    ...(result.ownedDrivers.items ?? []),
                    ...(result.freelanceDrivers.items ?? []),
                    ...(result.rentalDrivers.items ?? [])
                ]);
                this.availableVehicles = this.uniqueVehicles([
                    this.shipmentDelivery?.deliveryVehicle,
                    ...(result.ownedVehicles.items ?? []),
                    ...(result.freelanceVehicles.items ?? []),
                    ...(result.rentalVehicles.items ?? [])
                ]);
            });
    }

    private uniqueDrivers(drivers: Array<Driver | undefined | null>): Driver[] {
        const seen = new Set<number>();
        return drivers.filter((driver): driver is Driver => {
            if (!driver?.id || seen.has(driver.id)) {
                return false;
            }
            seen.add(driver.id);
            return true;
        });
    }

    private uniqueVehicles(vehicles: Array<Vehicle | undefined | null>): Vehicle[] {
        const seen = new Set<number>();
        return vehicles.filter((vehicle): vehicle is Vehicle => {
            if (!vehicle?.vehicleId || seen.has(vehicle.vehicleId)) {
                return false;
            }
            seen.add(vehicle.vehicleId);
            return true;
        });
    }

    driverOptionLabel(driver?: Driver | null): string {
        if (!driver) {
            return '';
        }
        return [driver.legalName || driver.name || 'Driver', driver.phoneNumber].filter(Boolean).join(' | ');
    }

    vehicleOptionLabel(vehicle?: Vehicle | null): string {
        if (!vehicle) {
            return '';
        }
        const type = [vehicle.config?.name, vehicle.capacity?.name].filter(Boolean).join(' ');
        return [vehicle.plateNumber || 'Vehicle', type].filter(Boolean).join(' | ');
    }

    replacementOwnershipLabel(resource?: Driver | Vehicle | null): string {
        return fleetResourceOwnershipTypeLabel(resource);
    }

    replacementOwnershipSeverity(resource?: Driver | Vehicle | null): 'success' | 'warn' | 'info' {
        switch ((resource?.resourceOwnershipType || '').toUpperCase()) {
            case 'OWNED':
                return 'success';
            case 'FREELANCE':
                return 'warn';
            default:
                return 'info';
        }
    }

    replacementDriverOwnerLine(driver?: Driver | null): string {
        if (!driver) {
            return '-';
        }
        return [fleetResourceOwnershipTypeLabel(driver), fleetResourceOwnerLabel(driver)].filter(Boolean).join(' | ');
    }

    replacementVehicleOwnerLine(vehicle?: Vehicle | null): string {
        if (!vehicle) {
            return '-';
        }
        return [fleetResourceOwnershipTypeLabel(vehicle), fleetResourceOwnerLabel(vehicle)].filter(Boolean).join(' | ');
    }

    replacementDriverSourceLine(driver?: Driver | null): string {
        if (!driver) {
            return '-';
        }
        return fleetResourceAgencyLabel(driver) || driver.resourceCompanyName || '-';
    }

    replacementVehicleSourceLine(vehicle?: Vehicle | null): string {
        if (!vehicle) {
            return '-';
        }
        return fleetResourceAgencyLabel(vehicle) || vehicle.resourceCompanyName || '-';
    }

    replacementDriverContextLine(driver?: Driver | null): string {
        if (!driver) {
            return '-';
        }
        return [
            driver.phoneNumber || 'No phone',
            driver.currentVehicle?.plateNumber || 'No linked vehicle'
        ].filter(Boolean).join(' | ');
    }

    replacementVehicleContextLine(vehicle?: Vehicle | null): string {
        if (!vehicle) {
            return '-';
        }
        return [
            [vehicle.config?.name, vehicle.capacity?.name].filter(Boolean).join(' ') || 'Type pending',
            vehicle.currentDriver?.legalName || vehicle.currentDriver?.name || 'No linked driver'
        ].filter(Boolean).join(' | ');
    }

    hasCrewAssetChange(): boolean {
        return (this.selectedReplacementDriver?.id ?? null) !== (this.shipmentDelivery?.deliveryDriver?.id ?? null)
            || (this.selectedReplacementVehicle?.vehicleId ?? null) !== (this.shipmentDelivery?.deliveryVehicle?.vehicleId ?? null);
    }

    replacementFinanceChannelLabel(): string {
        const ownership = String((this.selectedReplacementDriver ?? this.shipmentDelivery?.deliveryDriver)?.resourceOwnershipType || '')
            .toUpperCase();
        if (ownership === 'FREELANCE' || ownership === 'FREELANCER' || ownership === 'SUPPLIER') {
            return 'Payable to selected freelance driver';
        }
        if (ownership === 'TRANSPORTER') {
            return 'Payable to selected transporter';
        }
        return 'Internal bulk cost';
    }

    transportCostCurrencyCode(): string {
        return this.shipmentDelivery?.transportCostCurrencyCode || this.shipment?.currencyCode || 'SAR';
    }

    refreshCrewAssetNote(): void {
        this.crewAssetNoteHtml = this.defaultCrewAssetNote();
    }

    private noteHasText(noteHtml: string): boolean {
        return !!noteHtml?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    }

    private defaultCrewAssetNote(): string {
        const driverText = `${this.driverName()} -> ${this.driverOptionLabel(this.selectedReplacementDriver) || this.driverName()}`;
        const vehicleText = `${this.vehiclePlateNumber()} -> ${this.vehicleOptionLabel(this.selectedReplacementVehicle) || this.vehiclePlateNumber()}`;
        return `<p><strong>Driver/vehicle reassignment</strong></p><ul><li>Driver: ${driverText}</li><li>Vehicle: ${vehicleText}</li></ul>`;
    }

    isDeliveryTerminal(): boolean {
        const state = String(this.currentWorkflowState() || this.shipmentDelivery?.jobStatus || '').toUpperCase();
        return [
            'DELIVERY_COMPLETE',
            'CUSTOMER_ACKNOWLEDGED',
            'JOB_DRIVER_CANCELLED',
            'JOB_DRIVER_REJECTED',
            'CANCELLED',
            'REJECTED'
        ].includes(state);
    }

    private hasDeliverySiteExecutionStarted(): boolean {
        const activeSiteStates = new Set([
            'ARRIVED',
            'WORKING',
            'EVIDENCE_PENDING',
            'COMPLETE',
            'SKIPPED',
            'EXCEPTION'
        ]);
        if ((this.deliveryStops ?? []).some(stop =>
            Boolean(stop.actualArrival)
            || Boolean(stop.actualDeparture)
            || activeSiteStates.has(String(stop.workflowState || '').toUpperCase()))) {
            return true;
        }

        const state = String(this.currentWorkflowState() || this.shipmentDelivery?.jobStatus || '').toUpperCase();
        return [
            'DRIVER_REACHED_PICKUP',
            'JOB_PAYLOAD_LOADING',
            'PAYLOAD_QUANTITY_VERIFICATION',
            'PAYLOAD_CONDITION_CHECK',
            'DELIVERY_DOCUMENTS_UPLOAD',
            'PAYLOAD_SEALING_PROCESS',
            'DRIVER_ORDER_SIGNOFF',
            'DRIVER_STARTED_TRIP',
            'DRIVER_EN_ROUTE',
            'DRIVER_DELAY_ON_TRIP',
            'DRIVER_REACHED_DROPOFF',
            'RECIPIENT_VERIFICATION_PROCESS',
            'JOB_PAYLOAD_UNLOADING'
        ].includes(state);
    }

    deliveryChargeLines(): ShipmentChargeLine[] {
        return this.shipmentDelivery?.chargeLines ?? [];
    }

    totalBuyingCharges(): number {
        return this.deliveryChargeLines().reduce((sum, line) => sum + (line.buyingChargeAmount ?? 0), 0);
    }

    totalSellingCharges(): number {
        return this.deliveryChargeLines().reduce((sum, line) => sum + (line.sellingChargeAmount ?? 0), 0);
    }

    commercialMargin(): number {
        return this.totalSellingCharges() - this.totalBuyingCharges();
    }

    deliveryTransportChargeTotal(): number {
        return this.deliveryChargeLines()
            .filter(line => String(line.chargeType || '').toUpperCase() === 'TRANSPORT')
            .reduce((sum, line) => sum + (line.buyingChargeAmount ?? 0), 0);
    }

    deliveryOtherBuyingChargeTotal(): number {
        return this.totalBuyingCharges() - this.deliveryTransportChargeTotal();
    }

    deliveryAssignedTransportCost(): number {
        return this.deliveryUsesInternalTransport()
            ? (this.shipmentDelivery?.internalTransportCostAmount ?? 0)
            : (this.shipmentDelivery?.transportBuyingCostAmount ?? 0);
    }

    deliveryTransportCurrencyCode(): string | undefined {
        return this.shipmentDelivery?.transportCostCurrencyCode || this.currencyCode();
    }

    deliveryTransportSourceLabel(): string {
        const source = this.normalizedTransportFinancialSource();
        switch (source) {
            case 'INTERNAL_TRANSPORT_COST': return 'Truck-company internal cost';
            case 'SISTER_INTERNAL_TRANSPORT_COST': return 'Legacy sister-company cost';
            case 'CUSTOM_INTERNAL_TRANSPORT_COST': return 'Custom internal cost';
            case 'FREELANCE': return 'Freelance supplier';
            case 'RENTAL': return 'Rental supplier';
            case 'TRANSPORTER': return 'Transporter supplier';
            case 'SUPPLIER_DELIVERY_RATE_CARD': return 'Supplier rate card';
            case '': return 'Transport cost not set';
            default: return source.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, value => value.toUpperCase());
        }
    }

    deliveryMarginPercentageLabel(): string {
        const revenue = this.totalSellingCharges();
        if (revenue <= 0) {
            return 'No billable delivery revenue';
        }
        return `${((this.commercialMargin() / revenue) * 100).toFixed(1)}% delivery-level margin`;
    }

    deliveryAccountingRouteLabel(): string {
        const hasRevenue = this.totalSellingCharges() > 0;
        if (this.deliveryUsesInternalTransport()) return hasRevenue ? 'Receivable + bulk cost' : 'Bulk cost when posted';
        if (this.hasExternalTransportSource()) return hasRevenue ? 'Receivable + payable' : 'Payable when posted';
        return hasRevenue ? 'Receivable when posted' : 'No finance staging';
    }

    deliveryAccountingRouteTitle(): string {
        const label = this.deliveryAccountingRouteLabel();
        return label === 'No finance staging' ? 'Nothing to post' : label;
    }

    deliveryAccountingRouteDetail(): string {
        const label = this.deliveryAccountingRouteLabel();
        if (label === 'No finance staging') return 'Set a delivery transport cost or billable charge before posting.';
        if (label.includes('bulk cost')) return 'Internal transport creates bulk-cost staging in the truck-owning company when the shipment is posted.';
        if (label.includes('payable')) return 'Supplier transport creates payable staging in the truck-owning company when the shipment is posted.';
        return 'Billable delivery charges create receivable staging when the shipment is posted.';
    }

    deliveryAccountingRouteSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const label = this.deliveryAccountingRouteLabel();
        if (label === 'No finance staging') return 'secondary';
        if (label.includes('bulk cost')) return 'success';
        return 'info';
    }

    private normalizedTransportFinancialSource(): string {
        return (this.shipmentDelivery?.transportFinancialSource || '').replace(/^TRANSPORT_PLAN_/i, '').trim().toUpperCase();
    }

    private deliveryUsesInternalTransport(): boolean {
        return ['INTERNAL_TRANSPORT_COST', 'SISTER_INTERNAL_TRANSPORT_COST', 'CUSTOM_INTERNAL_TRANSPORT_COST', 'OWNED', 'SISTER_OWNED']
            .includes(this.normalizedTransportFinancialSource());
    }

    private hasExternalTransportSource(): boolean {
        return !!this.normalizedTransportFinancialSource() && !this.deliveryUsesInternalTransport();
    }

    formatChargeType(chargeLine: ShipmentChargeLine): string {
        return String(chargeLine.chargeType || 'Charge').replace(/_/g, ' ');
    }

    formatChargeAmount(chargeLine: ShipmentChargeLine, value: number | undefined): string {
        return this.formatAmount(chargeLine.chargeCurrency || this.currencyCode(), value ?? 0);
    }

    chargeMargin(chargeLine: ShipmentChargeLine): number {
        return (chargeLine.sellingChargeAmount ?? 0) - (chargeLine.buyingChargeAmount ?? 0);
    }

    chargeMarginPercent(chargeLine: ShipmentChargeLine): string {
        const selling = chargeLine.sellingChargeAmount ?? 0;
        if (selling <= 0) {
            return 'No selling value';
        }
        return `${((this.chargeMargin(chargeLine) / selling) * 100).toFixed(1)}% margin`;
    }

    deliveryExpenseRows(): { type: string; amount: number; percentage: number }[] {
        const totalBuying = this.totalBuyingCharges();
        if (totalBuying <= 0) {
            return [];
        }

        return this.deliveryChargeLines()
            .map(line => {
                const amount = line.buyingChargeAmount ?? 0;
                return {
                    type: String(line.chargeType || 'Other'),
                    amount,
                    percentage: Math.round((amount / totalBuying) * 100)
                };
            })
            .filter(row => row.amount > 0);
    }

    private setDeliveryUploadContext(): boolean {
        const shipmentId = this.shipment?.shipmentId;
        const deliveryId = this.shipmentDelivery?.deliveryId;
        if (!shipmentId || !deliveryId) {
            this.messageService.add({ severity: 'error', summary: 'Upload blocked', detail: 'Reload the delivery and try again.' });
            return false;
        }
        this.sessionStorageService.setShipmentUploadContext(shipmentId, deliveryId);
        return true;
    }

	addNewFile() {
		if (this.isCancelled()) {
			return;
		}
		if (!this.setDeliveryUploadContext()) {
            return;
        }

		// Define the callback function
		const fileUploadCallback: ShipmentFileDownloadUrlCallback = (file: ShipmentDeliveryAttachment, batchComplete?: boolean) => {
			this.shipmentDeliveryAttachments = [...this.shipmentDeliveryAttachments, file];
            this.shipmentDelivery.filesList = this.shipmentDeliveryAttachments;
			this.deliveryFilesCount = this.shipmentDeliveryAttachments.length;
			if (batchComplete) fileUploadDialog.close();
		};

		// Open the dialog with the callback
		let fileUploadDialog = this.dialogService.open(ShipmentFileUploadComponent, {
			header: 'Create/Upload Delivery File',
			width: 'min(48rem, calc(100vw - 1.5rem))',
			modal: true,
			data: { callback: fileUploadCallback },
		});
	}

    onDeliveryPayloadDocumentSelected(event: Event | File): void {
        if (this.isCancelled()) {
            return;
        }
        const input = event instanceof File ? undefined : event.target as HTMLInputElement;
        const file = event instanceof File ? event : input?.files?.[0];
        if (input) input.value = '';
        if (!file || !this.shipment?.shipmentId || !this.shipmentDelivery?.deliveryId) {
            return;
        }

        this.payloadExtractionInProgress = true;
        this.shipmentService.extractDeliveryPayload(this.shipment.shipmentId, this.shipmentDelivery.deliveryId, file).pipe(
            finalize(() => this.payloadExtractionInProgress = false)
        ).subscribe({
            next: result => {
                this.shipmentDelivery.payloadItemList = result.payloadItemList ?? [];
                if (result.customerOrderNumber) {
                    this.shipmentDelivery.customerOrderNumber = result.customerOrderNumber;
                }
                if (result.attachment) {
                    this.shipmentDeliveryAttachments = [...this.shipmentDeliveryAttachments, result.attachment];
                    this.shipmentDelivery.filesList = this.shipmentDeliveryAttachments;
                    this.deliveryFilesCount = this.shipmentDeliveryAttachments.length;
                }
                this.messageService.add({
                    severity: 'success',
                    summary: 'Payload extracted',
                    detail: `${this.payloadCount()} payload line${this.payloadCount() === 1 ? '' : 's'} applied.`
                });
            },
            error: error => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Extraction failed',
                    detail: error?.error?.message || 'Could not extract payload from the document.'
                });
            }
        });
    }

    onDeliveryPayloadChanged(result: ShipmentPayloadLineCommandResult): void {
        if (this.shipmentDelivery) {
            this.shipmentDelivery.payloadItemList = result.payloadItems ?? [];
        }
        if (result.containerCase) {
            this.loadDeliveryContainerCases();
        }
    }

    uploadHistoricalWaybill(): void {
        if (this.isCancelled() || !this.setDeliveryUploadContext()) return;
        this.historicalWaybillDialogVisible = true;
        this.historicalWaybillError = '';
        const dropoffs = this.dropoffStops().filter(stop => !!stop.stopId);
        this.historicalWaybillStopId = dropoffs.length === 1 ? dropoffs[0].stopId! : null;
        this.historicalWaybillSiteChanged();
        this.refreshHistoricalFiles();
    }

    historicalSiteOptions(): { label: string; value: number }[] {
        return this.dropoffStops().filter(stop => !!stop.stopId).map(stop => ({
            label: this.stopLabel(stop), value: stop.stopId!
        }));
    }

    historicalFileOptions(): { label: string; value: number }[] {
        const singleDropoff = this.dropoffStops().length === 1;
        return (this.shipmentDeliveryAttachments || [])
            .filter(file => file.attachmentId && file.fileUrl && String(file.fileType || '').trim().toLowerCase() === 'historical waybill'
                && (file.stopId === this.historicalWaybillStopId || (singleDropoff && !file.stopId)))
            .map(file => ({ label: file.fileName || 'Historical waybill', value: file.attachmentId! }));
    }

    historicalWaybillSiteChanged(): void {
        const stop = this.dropoffStops().find(item => item.stopId === this.historicalWaybillStopId);
        const site = this.deliveryWaybillSummary.deliveries.flatMap(delivery => delivery.sites || [])
            .find(item => item.stopId === this.historicalWaybillStopId);
        this.historicalWaybillExpected = stop
            ? (site?.waybillNumbers?.[0]?.trim() || stop.waybillNumber?.trim() || this.shipmentDelivery?.waybillNumber?.trim() || null) : null;
        this.historicalWaybillNumber = this.historicalWaybillExpected || '';
        this.historicalWaybillAttachmentId = null;
        if (this.historicalFileOptions().length === 1) this.historicalWaybillAttachmentId = this.historicalFileOptions()[0].value;
        this.historicalWaybillError = '';
    }

    refreshHistoricalWaybillNumber(): void {
        const shipmentId = this.shipment?.shipmentId;
        const deliveryId = this.shipmentDelivery?.deliveryId;
        const stopId = this.historicalWaybillStopId;
        if (!shipmentId || !deliveryId || !stopId || this.historicalNumberLoading) return;
        this.historicalNumberLoading = true;
        this.shipmentService.getDeliveryWaybills(shipmentId, deliveryId)
            .pipe(finalize(() => this.historicalNumberLoading = false)).subscribe({
                next: summary => {
                    if (stopId !== this.historicalWaybillStopId || deliveryId !== this.shipmentDelivery?.deliveryId) return;
                    this.deliveryWaybillSummary = summary;
                    const site = summary.deliveries.flatMap(delivery => delivery.sites || [])
                        .find(item => item.stopId === stopId);
                    if (!site) {
                        this.historicalWaybillError = 'The selected site is no longer available.';
                        return;
                    }
                    this.historicalWaybillExpected = site.waybillNumbers?.[0]?.trim() || null;
                    this.historicalWaybillError = '';
                },
                error: () => this.historicalWaybillError = 'Could not refresh the current number. Retry before saving.'
            });
    }

    uploadHistoricalFile(): void {
        const stopId = this.historicalWaybillStopId;
        if (!stopId || !this.setDeliveryUploadContext()) return;
        const callback: ShipmentFileDownloadUrlCallback = (file, batchComplete) => {
            this.refreshHistoricalFiles(file.fileUrl);
            if (batchComplete) fileUploadDialog.close();
        };
        const fileUploadDialog = this.dialogService.open(ShipmentFileUploadComponent, {
            header: 'Upload Historical Waybill File', width: 'min(48rem, calc(100vw - 1.5rem))', modal: true,
            data: { callback, defaultFileType: 'Historical Waybill', lockFileType: true, allowMultiple: false, stopId }
        });
    }

    refreshHistoricalFiles(selectedUrl?: string): void {
        const shipmentId = this.shipment?.shipmentId;
        const deliveryId = this.shipmentDelivery?.deliveryId;
        if (!shipmentId || !deliveryId) return;
        this.historicalFilesLoading = true;
        this.historicalFilesReady = false;
        this.shipmentService.getDeliveryAttachments(shipmentId, deliveryId)
            .pipe(finalize(() => this.historicalFilesLoading = false)).subscribe({
                next: files => {
                    if (deliveryId !== this.shipmentDelivery?.deliveryId) return;
                    this.shipmentDeliveryAttachments = files || [];
                    this.historicalFilesReady = true;
                    this.historicalWaybillError = '';
                    this.shipmentDelivery.filesList = this.shipmentDeliveryAttachments;
                    this.shipmentDelivery.isPhysicalWaybillReceived = this.hasHistoricalWaybill();
                    this.deliveryFilesCount = this.shipmentDeliveryAttachments.length;
                    const uploaded = selectedUrl ? files.find(file => file.fileUrl === selectedUrl) : undefined;
                    const options = this.historicalFileOptions();
                    if (uploaded?.attachmentId && options.some(item => item.value === uploaded.attachmentId)) {
                        this.historicalWaybillAttachmentId = uploaded.attachmentId;
                    } else if (!options.some(item => item.value === this.historicalWaybillAttachmentId)) {
                        this.historicalWaybillAttachmentId = options.length === 1 ? options[0].value : null;
                    }
                },
                error: () => this.historicalWaybillError = 'Could not load historical files. Retry before saving.'
            });
    }

    saveHistoricalWaybillNumber(): void {
        const shipmentId = this.shipment?.shipmentId;
        const deliveryId = this.shipmentDelivery?.deliveryId;
        const stopId = this.historicalWaybillStopId;
        const attachmentId = this.historicalWaybillAttachmentId;
        const number = this.historicalWaybillNumber.trim();
        if (!shipmentId || !deliveryId || !stopId || !attachmentId || !number || !this.historicalFilesReady || this.historicalWaybillBusy || this.historicalNumberLoading) return;
        const replacing = !!this.historicalWaybillExpected && this.historicalWaybillExpected !== number;
        if (replacing && !window.confirm(`Replace waybill ${this.historicalWaybillExpected} with ${number}?`)) return;
        this.historicalWaybillError = '';
        this.historicalWaybillBusy = true;
        this.shipmentService.saveHistoricalWaybillNumber(shipmentId, deliveryId, stopId, {
            waybillNumber: number, attachmentId, expectedWaybillNumber: this.historicalWaybillExpected,
            confirmReplacement: replacing
        }).pipe(finalize(() => this.historicalWaybillBusy = false)).subscribe({
            next: result => {
                const stop = this.deliveryStops.find(item => item.stopId === result.stopId);
                if (stop) stop.waybillNumber = result.waybillNumber;
                this.shipmentDelivery.waybillNumber = result.deliveryWaybillNumber || undefined;
                this.loadDeliveryWaybills();
                this.historicalWaybillDialogVisible = false;
                this.messageService.add({ severity: 'success', summary: 'Waybill saved', detail: 'Historical waybill number saved.' });
            },
            error: error => this.historicalWaybillError = error?.error?.message || error?.error?.detail || 'The number could not be saved. Retry or reopen to refresh.'
        });
    }

    uploadPodForStop(stop: ShipmentDeliveryStop): void {
        if (this.isCancelled()) {
            return;
        }
        if (!stop.stopId) {
            this.messageService.add({ severity: 'warn', summary: 'Stop missing', detail: 'Save the delivery stop before uploading POD.' });
            return;
        }
        if (!this.setDeliveryUploadContext()) {
            return;
        }

        const fileUploadCallback: ShipmentFileDownloadUrlCallback = (file: ShipmentDeliveryAttachment, batchComplete?: boolean) => {
            const podFile = { ...file, stopId: stop.stopId, fileType: file.fileType || 'Proof of Delivery' };
            this.shipmentDeliveryAttachments = [...this.shipmentDeliveryAttachments, podFile];
            this.shipmentDelivery.filesList = this.shipmentDeliveryAttachments;
            this.deliveryFilesCount = this.shipmentDeliveryAttachments.length;
            stop.evidenceStatus = 'VERIFIED';
            if (batchComplete) fileUploadDialog.close();
        };

        const fileUploadDialog = this.dialogService.open(ShipmentFileUploadComponent, {
            header: `Upload POD - ${this.stopLabel(stop)}`,
            width: 'min(48rem, calc(100vw - 1.5rem))',
            modal: true,
            data: {
                callback: fileUploadCallback,
                defaultFileType: 'Proof of Delivery',
                lockFileType: true,
                stopId: stop.stopId
            }
        });
    }

    dropoffStops(): ShipmentDeliveryStop[] {
        return (this.deliveryStops ?? [])
            .filter(stop => String(stop.stopType || '').toUpperCase() === 'DROPOFF')
            .sort((left, right) => Number(left.stopNumber ?? 0) - Number(right.stopNumber ?? 0));
    }

    podAttachmentsForStop(stop: ShipmentDeliveryStop): ShipmentDeliveryAttachment[] {
        return (this.shipmentDeliveryAttachments ?? [])
            .filter(file => file.stopId === stop.stopId)
            .filter(file => this.isPodAttachment(file));
    }

    deliveryLevelPodAttachments(): ShipmentDeliveryAttachment[] {
        return (this.shipmentDeliveryAttachments ?? [])
            .filter(file => !file.stopId)
            .filter(file => this.isPodAttachment(file));
    }

    stopLabel(stop: ShipmentDeliveryStop): string {
        return `${stop.stopType || 'STOP'} #${stop.stopNumber || '-'}${stop.locationName ? ' - ' + stop.locationName : ''}`;
    }

    attachmentStop(file: ShipmentDeliveryAttachment): ShipmentDeliveryStop | undefined {
        if (file.stopId) {
            return this.deliveryStops.find(stop => Number(stop.stopId) === Number(file.stopId));
        }
        return this.inferAttachmentStopFromName(file);
    }

    attachmentStopLabel(file: ShipmentDeliveryAttachment): string {
        const stop = this.attachmentStop(file);
        if (stop) {
            return `${this.displayState(stop.stopType || 'Stop')} #${stop.stopNumber || '-'}`;
        }
        return 'Delivery level';
    }

    attachmentStopSubtitle(file: ShipmentDeliveryAttachment): string {
        const stop = this.attachmentStop(file);
        if (stop) {
            return stop.locationName || 'Stop-specific file';
        }
        return file.stopId ? `Stop ID ${file.stopId}` : 'Applies to the full route';
    }

    attachmentStopIcon(file: ShipmentDeliveryAttachment): string {
        const stop = this.attachmentStop(file);
        const stopType = String(stop?.stopType || '').toUpperCase();
        if (stopType === 'PICKUP') return 'fa-solid fa-arrow-up-from-bracket';
        if (stopType === 'DROPOFF') return 'fa-solid fa-arrow-down-to-bracket';
        return file.stopId ? 'fa-solid fa-location-dot' : 'fa-solid fa-route';
    }

    private inferAttachmentStopFromName(file: ShipmentDeliveryAttachment): ShipmentDeliveryStop | undefined {
        const name = String(file.fileName || '').toLowerCase();
        const match = name.match(/(?:^|[_\-\s])(pickup|pick|dropoff|drop|stop)[_\-\s]*(\d+)(?:[_\-\s.]|$)/);
        if (!match) {
            return undefined;
        }
        const inferredType = match[1].startsWith('pick')
            ? 'PICKUP'
            : match[1].startsWith('drop')
                ? 'DROPOFF'
                : undefined;
        const stopNumber = Number(match[2]);
        return this.deliveryStops.find(stop =>
            Number(stop.stopNumber) === stopNumber
            && (!inferredType || String(stop.stopType || '').toUpperCase() === inferredType)
        ) ?? this.deliveryStops.find(stop => Number(stop.stopNumber) === stopNumber);
    }

    private isPodAttachment(file: ShipmentDeliveryAttachment): boolean {
        return String(file.fileType || '').toLowerCase() === 'proof of delivery'
            || String(file.fileName || '').toLowerCase().includes('pod');
    }

    reprintWaybill(): void {
        if (this.isCancelled()) {
            return;
        }
        if (this.hasHistoricalWaybill()) {
            this.messageService.add({
                severity: 'info',
                summary: 'Historical waybill active',
                detail: 'Cargomate waybill generation is suppressed for this delivery.'
            });
            return;
        }
        if (!this.shipment || !this.shipmentDelivery) {
            this.messageService.add({ severity: 'warn', summary: 'Waybill unavailable', detail: 'Delivery details are not loaded yet.' });
            return;
        }

        this.waybillReprintInProgress = true;
        this.shipmentService.regenerateDeliveryWaybills(this.shipment.shipmentId ?? 0, this.shipmentDelivery.deliveryId ?? 0)
            .pipe(finalize(() => this.waybillReprintInProgress = false))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Waybill generated', detail: 'Waybill has been reprinted.' });
                    this.refreshDeliverySnapshot();
                },
                error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to reprint waybill.' })
            });
    }

    generateDeliveryNote(): void {
        if (this.isCancelled()) {
            return;
        }
        const shipmentId = this.shipment?.shipmentId;
        const deliveryId = this.shipmentDelivery?.deliveryId;
        if (!shipmentId || !deliveryId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Delivery unavailable',
                detail: 'Delivery details are not loaded yet.'
            });
            return;
        }

        this.deliveryNoteGenerationInProgress = true;
        this.shipmentService.generateDeliveryNotes(shipmentId, deliveryId)
            .pipe(finalize(() => this.deliveryNoteGenerationInProgress = false))
            .subscribe({
                next: (attachments) => {
                    const count = attachments?.length ?? 0;
                    this.messageService.add({
                        severity: 'success',
                        summary: count > 1 ? 'Delivery notes generated' : 'Delivery note generated',
                        detail: count > 1
                            ? `${count} release-scoped delivery notes have been generated.`
                            : 'Shipment delivery note has been generated.'
                    });
                    this.refreshDeliverySnapshot();
                    this.openExternalUrl(attachments?.[0]?.fileUrl);
                },
                error: (error) => {
                    const message = error?.error?.message || 'Failed to generate delivery note.';
                    this.messageService.add({ severity: 'error', summary: 'Generation failed', detail: message });
                }
            });
    }

    openExternalUrl(url?: string): void {
        if (!url) {
            return;
        }
        window.open(url, '_blank', 'noopener');
    }

    deleteAttachment(file: ShipmentDeliveryAttachment): void {
        if (!file.attachmentId || !window.confirm('Delete this attachment?')) {
            return;
        }
        this.shipmentService.deleteAttachment(file.attachmentId).subscribe({
            next: () => {
                this.shipmentDeliveryAttachments = this.shipmentDeliveryAttachments
                    .filter(attachment => attachment.attachmentId !== file.attachmentId);
                this.shipmentDelivery.filesList = this.shipmentDeliveryAttachments;
                this.shipmentDelivery.isPhysicalWaybillReceived = this.shipmentDeliveryAttachments
                    .some(attachment => isHistoricalWaybillAttachment(attachment));
                this.deliveryFilesCount = this.shipmentDeliveryAttachments.length;
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Attachment removed.' });
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete attachment.' })
        });
    }

	activeTabIndex = 0;
	onTabChange(event: { index: number | string }): void {
		this.activeTabIndex = Number(event.index);
        this.updateDeliveryTabQueryParam();
		this.loadActiveDeliveryTabData();
	}

    private loadActiveDeliveryTabData(): void {
        if (!this.shipmentDelivery?.deliveryId || this.shipmentDelivery.deliveryId !== this.deliveryId) return;
        if (this.activeTabIndex === 6 && !this.deliveryNotesLoaded && !this.deliveryNotesLoading) {
            this.loadDeliveryNotes();
        }
        if (this.activeTabIndex === 3 && !this.deliveryFinanceRequested) {
            this.loadDeliveryFinance();
        }
    }

    private resolveDeliveryTabValue(tabKey: string | null): number {
        if (!tabKey) {
            return 0;
        }

        const normalizedTabKey = tabKey.toLowerCase();
        if (normalizedTabKey === 'charges' && !this.canViewShipmentPricing()) {
            return 0;
        }

        const selectedTab = this.deliveryTabKeys.find(tab => tab.key === normalizedTabKey);
        if (!selectedTab) {
            return 0;
        }
        if (selectedTab.key === 'charges' && !this.canViewShipmentPricing()) {
            return 0;
        }
        if (selectedTab.key === 'field-work' && !this.hasDeliveryFieldWork() && !this.deliveryFieldWorkError) {
            return 0;
        }
        return selectedTab.value;
    }

    private updateDeliveryTabQueryParam(): void {
        const selectedTab = this.deliveryTabKeys.find(tab => tab.value === this.activeTabIndex);
        if (!selectedTab) {
            return;
        }
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: selectedTab.key },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    retryDeliveryNotes(): void {
        this.loadDeliveryNotes();
    }

    private loadDeliveryNotes(): void {
        if (!this.shipment?.shipmentId || !this.shipmentDelivery?.deliveryId) {
            return;
        }
        const deliveryId = this.deliveryId;
        this.deliveryNotesLoading = true;
        this.deliveryNotesError = false;
        forkJoin({
            remark: this.shipmentService.getDeliveryRemark(this.shipment.shipmentId, deliveryId),
            feed: this.shipmentService.getDeliveryRemarksFeed(this.shipment.shipmentId, deliveryId)
        }).subscribe({
            next: ({ remark, feed }) => {
                if (deliveryId !== this.deliveryId) return;
                this.deliveryRemark = remark ?? {};
                this.shipmentDelivery.remark = this.deliveryRemark;
                this.deliveryRemarksHtml = this.deliveryRemark.remarksHtml ?? '';
                this.deliveryRemarksFeed = feed ?? { entries: [] };
                this.deliveryNotesLoaded = true;
                this.deliveryNotesLoading = false;
            },
            error: () => {
                if (deliveryId !== this.deliveryId) return;
                this.deliveryNotesLoading = false;
                this.deliveryNotesError = true;
            }
        });
    }

    retryDeliveryWaybills(): void {
        this.loadDeliveryWaybills();
    }

    private loadDeliveryWaybills(): void {
        if (!this.shipment?.shipmentId || !this.shipmentDelivery?.deliveryId) {
            return;
        }
        const deliveryId = this.deliveryId;
        this.deliveryWaybillsLoading = true;
        this.deliveryWaybillsError = false;
        this.shipmentService.getDeliveryWaybills(this.shipment.shipmentId, this.shipmentDelivery.deliveryId).subscribe({
            next: summary => {
                if (deliveryId !== this.deliveryId) return;
                this.deliveryWaybillSummary = summary ?? { deliveries: [] };
                this.deliveryWaybillsLoading = false;
            },
            error: () => {
                if (deliveryId !== this.deliveryId) return;
                this.deliveryWaybillsLoading = false;
                this.deliveryWaybillsError = true;
            }
        });
    }

    saveDeliveryRemarks(): void {
        if (this.isCancelled() || !this.shipment?.shipmentId || !this.shipmentDelivery?.deliveryId) {
            return;
        }
        this.saveDeliveryRemarksInProgress = true;
        this.shipmentService.saveDeliveryRemark(this.shipment.shipmentId, this.shipmentDelivery.deliveryId, {
            ...this.deliveryRemark,
            shipmentId: this.shipment.shipmentId,
            deliveryId: this.shipmentDelivery.deliveryId,
            remarksHtml: this.deliveryRemarksHtml,
            updatedByCustomerId: this.sessionStorageService.getCustomer()?.id
        }).subscribe({
            next: remark => {
                this.deliveryRemark = remark;
                this.shipmentDelivery.remark = remark;
                this.deliveryRemarksHtml = remark.remarksHtml ?? '';
                this.deliveryNotesLoaded = false;
                this.loadDeliveryNotes();
                this.saveDeliveryRemarksInProgress = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Delivery notes updated.' });
            },
            error: () => {
                this.saveDeliveryRemarksInProgress = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save delivery notes.' });
            }
        });
    }

	addEvent(event: any) {
		if (this.isCancelled() || !event.workflowTarget || !this.shipment?.shipmentId) {
			return;
		}
        this.transitionDeliveryWorkflow(event);
    }

    private transitionDeliveryWorkflow(event: any, overrideReason?: string): void {
        if (this.isCancelled()) {
            return;
        }
        this.shipmentService.transitionDeliveryWorkflow(this.shipment.shipmentId!, this.deliveryId, {
            targetState: event.workflowTarget,
            actionCode: event.content || event.workflowTarget,
            source: 'PORTAL',
            actorCustomerId: this.sessionStorageService.getCustomer()?.id,
            overrideReason
        }).subscribe({
            next: (workflowEvent) => {
                this.shipmentDelivery.workflowState = workflowEvent.nextState;
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `${event.content || event.workflowTarget} recorded.` });
                this.refreshDeliverySnapshot();
                this.refreshWorkflowData();
            },
            error: (error) => {
                if (overrideReason) {
                    const message = error?.error?.message || 'Workflow override was rejected.';
                    this.messageService.add({ severity: 'error', summary: 'Override rejected', detail: message });
                    return;
                }
                this.handleWorkflowTransitionError(event, error);
            }
        });
    }

    openHistoricalCompletionDialog(): void {
        if (this.isCancelled() || !this.canMarkHistoricalCompletion()) {
            return;
        }
        this.historicalCompletionDate = null;
        this.historicalCompletionOverrideReason = '';
        this.historicalCompletionDialogVisible = true;
    }

    submitHistoricalCompletion(): void {
        if (this.isCancelled()) {
            return;
        }
        const shipmentId = this.shipment?.shipmentId;
        const deliveryId = this.shipmentDelivery?.deliveryId;
        const actorCustomerId = this.sessionStorageService.getCustomer()?.id;
        if (!shipmentId || !deliveryId) {
            this.messageService.add({ severity: 'warn', summary: 'Delivery unavailable', detail: 'Delivery details are not loaded yet.' });
            return;
        }
        if (!this.historicalCompletionDate) {
            this.messageService.add({ severity: 'warn', summary: 'Completion time required', detail: 'Select the historical completion date and time.' });
            return;
        }
        if (!actorCustomerId) {
            this.messageService.add({ severity: 'warn', summary: 'Actor missing', detail: 'Your session is missing the actor context.' });
            return;
        }

        const request: HistoricalDeliveryCompletionRequest = {
            completedAt: this.historicalCompletionDate.getTime(),
            actorCustomerId,
            overrideReason: this.historicalCompletionOverrideReason?.trim() || undefined
        };

        this.historicalCompletionInProgress = true;
        this.shipmentService.completeDeliveryHistorically(shipmentId, deliveryId, request)
            .pipe(finalize(() => this.historicalCompletionInProgress = false))
            .subscribe({
                next: () => {
                    this.historicalCompletionDialogVisible = false;
                    this.messageService.add({ severity: 'success', summary: 'Delivery completed', detail: 'Historical delivery completion has been recorded.' });
                    this.refreshDeliverySnapshot();
                    this.refreshWorkflowData();
                },
                error: (error) => {
                    const message = error?.error?.message || 'Historical delivery completion failed.';
                    this.messageService.add({ severity: 'error', summary: 'Completion failed', detail: message });
                }
            });
    }

    private handleWorkflowTransitionError(event: any, error: any): void {
        const message = error?.error?.message || 'Transition blocked by workflow gates.';
        this.messageService.add({ severity: 'warn', summary: 'Gate failed', detail: message });
        if (!this.permissionService.hasPermission(app_permissions.shipments.workflow_override)) {
            return;
        }
        const reason = window.prompt(`${message}\n\nEnter override reason to continue:`);
        if (reason?.trim()) {
            this.transitionDeliveryWorkflow(event, reason.trim());
        }
    }

    private refreshDeliverySnapshot(): void {
        if (!this.deliveryId) {
            return;
        }
        const requestedDeliveryId = this.deliveryId;
        const requestToken = ++this.deliveryLoadRequestToken;
        this.shipmentService.getShipmentAndDeliveryByDeliveryId(requestedDeliveryId).subscribe({
            next: shipment => {
                if (requestToken !== this.deliveryLoadRequestToken || requestedDeliveryId !== this.deliveryId) {
                    return;
                }
                const delivery = shipment?.deliveries?.find(item => item.deliveryId === requestedDeliveryId);
                if (!shipment || !delivery) {
                    return;
                }
                this.shipment = shipment;
                this.shipmentDelivery = delivery;
                this.shipmentDeliveryAttachments = delivery.filesList ?? [];
                this.deliveryFilesCount = this.shipmentDeliveryAttachments.length;
                this.loadOperationStatus();
            }
        });
    }

	addNewChargeLine() {
		if (this.isCancelled()) {
			return;
		}
		this.selectedChargeLine = {
			shipmentId: this.shipment.shipmentId,
			deliveryId: this.deliveryId,
			buyingChargeAmount: undefined,
			chargeType: undefined,
			remarks: undefined,
			sellingChargeAmount: undefined,
			chargeCurrency: this.shipment.countryOfOrder?.currencyCode ?? 'SAR',
			effectiveDate: new Date().getTime()
		};
		this.chargeLineDialogVisible = true;
	}

	editChargeLine(chargeLine: ShipmentChargeLine) {
		if (this.isCancelled()) {
			return;
		}
		this.selectedChargeLine = { ...chargeLine };
		this.chargeLineDialogVisible = true;
	}

	hideChargeLineDialog() {
		this.chargeLineDialogVisible = false;
	}

	saveChargeLine(request: ShipmentChargeLine | ShipmentChargeLineBulkCreateRequest) {
		if (this.isCancelled()) {
			return;
		}
		this.saveChargeLineInProgress = true;
		if (this.isBulkChargeLineRequest(request)) {
			this.shipmentService.addChargeLines(this.shipment.shipmentId ?? 0, request).subscribe({
				next: savedLines => this.completeChargeLineSave(savedLines, `${savedLines.length} delivery charge lines saved`),
				error: error => this.showChargeLineSaveError(error)
			}).add(() => this.saveChargeLineInProgress = false);
			return;
		}
		this.shipmentService.addOrUpdateChargeLine(this.shipment.shipmentId ?? 0, request).subscribe({
			next: savedChargeLine => this.completeChargeLineSave([savedChargeLine ?? request], 'Charge line saved'),
			error: error => this.showChargeLineSaveError(error)
		}).add(() => this.saveChargeLineInProgress = false);
	}

	private isBulkChargeLineRequest(
		request: ShipmentChargeLine | ShipmentChargeLineBulkCreateRequest
	): request is ShipmentChargeLineBulkCreateRequest {
		return 'deliveryIds' in request || 'deliveryCharges' in request;
	}

	private completeChargeLineSave(savedLines: ShipmentChargeLine[], detail: string): void {
		for (const lineToSave of savedLines) {
			this.shipment.chargeLines = this.shipment.chargeLines ?? [];
			this.shipmentDelivery.chargeLines = this.shipmentDelivery.chargeLines ?? [];

			const index = this.shipment.chargeLines.findIndex(c => c.chargeLineId === lineToSave.chargeLineId);
			if (index !== -1) {
				this.shipment.chargeLines[index] = lineToSave;
			} else {
				this.shipment.chargeLines.push(lineToSave);
			}

			if (lineToSave.deliveryId === this.shipmentDelivery.deliveryId) {
				const deliveryIndex = this.shipmentDelivery.chargeLines.findIndex(c => c.chargeLineId === lineToSave.chargeLineId);
				if (deliveryIndex !== -1) {
					this.shipmentDelivery.chargeLines[deliveryIndex] = lineToSave;
				} else {
					this.shipmentDelivery.chargeLines.push(lineToSave);
				}
			}
		}
		this.recalculateLocalDeliveryWorth();
		this.messageService.add({ severity: 'success', summary: 'Success', detail });
		this.hideChargeLineDialog();
        this.refreshDeliverySnapshot();
	}

	private showChargeLineSaveError(error: any): void {
		this.messageService.add({
			severity: 'error',
			summary: 'Charge line not saved',
			detail: error?.error?.message || 'Review the charge details and try again.'
		});
	}

    private recalculateLocalDeliveryWorth(): void {
        const deliveryChargeLines = this.shipmentDelivery?.chargeLines ?? [];
        this.shipmentDelivery.jobWorth = deliveryChargeLines.reduce((sum, line) => sum + (line.buyingChargeAmount ?? 0), 0);
        this.shipmentDelivery.fareQuote = deliveryChargeLines.reduce((sum, line) => sum + (line.sellingChargeAmount ?? 0), 0);

        const shipmentChargeLines = this.shipment?.chargeLines ?? [];
        this.shipment.totalJobWorth = shipmentChargeLines.reduce((sum, line) => sum + (line.buyingChargeAmount ?? 0), 0);
        this.shipment.totalFareQuote = shipmentChargeLines.reduce((sum, line) => sum + (line.sellingChargeAmount ?? 0), 0);
    }

    loadOperationStatus(): void {
        if (!this.shipment?.shipmentId || !this.deliveryId) {
            return;
        }
        this.deliveryFieldWorkLocationOptions = [];
        this.shipmentService.getDeliveryOperationStatus(this.shipment.shipmentId, this.deliveryId).subscribe({
            next: status => {
                this.operationStatus = status;
                this.operationTimelineEvents = status?.timelineEvents ?? [];
                this.deliveryStops = (status?.stops ?? []).map(stop => ({
                    stopId: stop.stopId,
                    shipmentId: this.shipment.shipmentId,
                    deliveryId: this.deliveryId,
                    stopNumber: stop.stopNumber,
                    stopType: stop.stopType,
                    stopOrigin: stop.stopOrigin,
                    stopPurpose: stop.stopPurpose,
                    returnDestinationConfirmedAt: stop.returnDestinationConfirmedAt,
                    locationId: stop.locationId,
                    locationName: stop.locationName,
                    waybillNumber: stop.waybillNumber,
                    workflowState: stop.workflowState,
                    evidenceStatus: stop.evidenceStatus,
                    scheduledArrival: stop.scheduledArrival,
                    actualArrival: stop.actualArrival,
                    scheduledDeparture: stop.scheduledDeparture,
                    actualDeparture: stop.actualDeparture
                } as ShipmentDeliveryStop));
                if (this.historicalWaybillDialogVisible && !this.historicalWaybillStopId) {
                    const dropoffs = this.dropoffStops().filter(stop => !!stop.stopId);
                    if (dropoffs.length === 1) {
                        this.historicalWaybillStopId = dropoffs[0].stopId!;
                        this.historicalWaybillSiteChanged();
                    }
                }
                this.deliveryFieldWorkLocationOptions = this.deliveryStops
                    .filter(stop => Boolean(stop.locationId))
                    .sort((left, right) => (left.stopNumber ?? Number.MAX_SAFE_INTEGER)
                        - (right.stopNumber ?? Number.MAX_SAFE_INTEGER))
                    .map(stop => ({
                        id: stop.locationId!,
                        name: stop.locationName || `Location #${stop.locationId}`,
                        stopType: stop.stopType,
                        stopNumber: stop.stopNumber,
                        fieldWorkLabel: `${(stop.stopType || 'STOP').replaceAll('_', ' ')} #${stop.stopNumber ?? '-'} — ${stop.locationName || `Location #${stop.locationId}`}`
                    }));
                if (status?.deliveryStatus) {
                    this.shipmentDelivery.workflowState = status.deliveryStatus;
                }
            },
            error: () => {
                this.deliveryFieldWorkLocationOptions = [];
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load delivery operation status.' });
            }
        });
    }

    openSiteOperation(stop: ShipmentDeliveryStop): void {
        if (!stop?.stopId) {
            return;
        }
        this.router.navigate(['/shipment/delivery/view', this.deliveryId, 'site', stop.stopId]);
    }

    canManageDeliveryOnlyStops(): boolean {
        if (this.isCancelled()) {
            return false;
        }
        return this.permissionService.hasPermission(app_permissions.shipments.deliveries.manage_stops)
            || this.permissionService.hasPermission(app_permissions.shipments.deliveries.edit);
    }

    canEditDeliveryOnlyStop(stop: ShipmentDeliveryStop): boolean {
        return this.canManageDeliveryOnlyStops()
            && String(stop.stopOrigin || '').toUpperCase() === 'DELIVERY_AD_HOC'
            && stop.stopPurpose !== 'REJECTION_RETURN'
            && String(stop.workflowState || 'PENDING').toUpperCase() === 'PENDING';
    }

    openReturnDestinationDialog(stop: ShipmentDeliveryStop): void {
        if (!this.canManageDeliveryOnlyStops() || stop.stopPurpose !== 'REJECTION_RETURN'
            || stop.returnDestinationConfirmedAt || !stop.stopId) return;
        this.selectedReturnStop = stop;
        this.returnDestinationLocationId = stop.locationId;
        this.returnDestinationError = '';
        this.returnDestinationDialogVisible = true;
    }

    confirmReturnDestination(): void {
        const stopId = this.selectedReturnStop?.stopId;
        const shipmentId = this.shipment?.shipmentId;
        if (!shipmentId || !stopId || !this.returnDestinationLocationId || !this.canManageDeliveryOnlyStops()) return;
        this.returnDestinationSaving = true;
        this.returnDestinationError = '';
        this.shipmentService.confirmRejectionReturn(shipmentId, this.deliveryId, stopId, this.returnDestinationLocationId)
            .pipe(finalize(() => this.returnDestinationSaving = false)).subscribe({
                next: () => {
                    this.returnDestinationDialogVisible = false;
                    this.selectedReturnStop = undefined;
                    this.messageService.add({ severity: 'success', summary: 'Return destination confirmed', detail: 'The driver can now navigate to the return site.' });
                    this.loadOperationStatus();
                },
                error: error => this.returnDestinationError = error?.status === 404
                    ? 'Return confirmation is not available on this server yet. Please try again after the service is updated.'
                    : error?.error?.message || error?.message || 'Could not confirm the return destination.'
            });
    }

    openDeliveryOnlyStopDialog(stop?: ShipmentDeliveryStop): void {
        if (!this.canManageDeliveryOnlyStops()) return;
        this.editingDeliveryOnlyStop = stop;
        this.deliveryOnlyStopWaypoint = Object.assign(new ShipmentWaypoint(), {
            location: stop?.locationId ? { id: stop.locationId, name: stop.locationName } : undefined,
            waypointType: 'INTERMEDIATE',
            remarks: stop?.remarks
        });
        this.deliveryOnlyStopDialogVisible = true;
    }

    hideDeliveryOnlyStopDialog(): void {
        if (!this.deliveryOnlyStopSaving) this.deliveryOnlyStopDialogVisible = false;
    }

    saveDeliveryOnlyStop(waypoint: ShipmentWaypoint): void {
        if (this.isCancelled()) return;
        const shipmentId = this.shipment?.shipmentId;
        const locationId = waypoint?.location?.id;
        if (!shipmentId || !this.deliveryId || !locationId) return;
        const request = { locationId, remarks: waypoint.remarks };
        this.deliveryOnlyStopSaving = true;
        const save$ = this.editingDeliveryOnlyStop?.stopId
            ? this.shipmentService.updateDeliveryOnlyStop(shipmentId, this.deliveryId, this.editingDeliveryOnlyStop.stopId, request)
            : this.shipmentService.createDeliveryOnlyStop(shipmentId, this.deliveryId, request);
        save$.pipe(finalize(() => this.deliveryOnlyStopSaving = false)).subscribe({
            next: () => {
                this.deliveryOnlyStopDialogVisible = false;
                this.messageService.add({ severity: 'success', summary: 'Operational stop saved', detail: 'The delivery route was updated without changing the shipment route.' });
                this.loadOperationStatus();
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Unable to save operational stop', detail: error?.error?.message || 'Please correct the location and try again.' })
        });
    }

    removeDeliveryOnlyStop(stop: ShipmentDeliveryStop): void {
        if (this.isCancelled()) return;
        const shipmentId = this.shipment?.shipmentId;
        if (!shipmentId || !this.deliveryId || !stop.stopId || !this.canEditDeliveryOnlyStop(stop)) return;
        if (!window.confirm(`Remove ${stop.locationName || 'this operational stop'} from this delivery?`)) return;
        this.shipmentService.deleteDeliveryOnlyStop(shipmentId, this.deliveryId, stop.stopId).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Operational stop removed', detail: 'The shipment route was not changed.' });
                this.loadOperationStatus();
            },
            error: error => this.messageService.add({ severity: 'error', summary: 'Unable to remove operational stop', detail: error?.error?.message || 'Only pending delivery-only stops can be removed.' })
        });
    }

    refreshWorkflowData(): void {
        this.loadOperationStatus();
    }

    workflowDate(state: string): string | undefined {
        const event = [...this.operationTimelineEvents].reverse().find(item => item.level === 'DELIVERY' && item.nextState === state);
        if (!event?.eventAt) {
            return undefined;
        }
        const value = event.eventAt instanceof Date ? event.eventAt.getTime() : new Date(event.eventAt).getTime();
        return this.showDate(value);
    }

    operationalTimelineGroups(): OperationalTimelineGroup[] {
        const groups = new Map<string, OperationalTimelineGroup>();
        for (const event of this.operationTimelineEvents ?? []) {
            const stop = this.deliveryStops.find(item => item.stopId === event.stopId);
            const key = stop
                ? `stop-${stop.stopId}`
                : event.deliveryId
                    ? `delivery-${event.deliveryId}`
                    : 'shipment';
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    title: stop
                        ? `${stop.stopType || 'STOP'} #${stop.stopNumber || '-'}`
                        : event.deliveryId
                            ? `Delivery ${this.shipmentDelivery?.deliveryNumber || event.deliveryId}`
                            : `Shipment ${this.shipment?.shipmentNumber || event.shipmentId || ''}`.trim(),
                    subtitle: stop?.locationName || (stop?.locationId ? `Location ${stop.locationId}` : event.level || 'Status update'),
                    events: []
                });
            }
            groups.get(key)!.events.push(event);
        }
        return Array.from(groups.values());
    }

    workflowEventLabel(event: ShipmentOperationTimelineEvent): string {
        if (event.title) {
            return event.title;
        }
        const level = (event.level || 'STATUS').replace('_', ' ');
        const previous = event.previousState || 'START';
        const next = event.nextState || 'UPDATED';
        return `${level}: ${previous} -> ${next}`;
    }

    workflowEventMeta(event: ShipmentOperationTimelineEvent): string {
        return [event.description, event.overridden ? 'Override' : null]
            .filter((value): value is string => !!value)
            .join(' / ') || 'Status update';
    }

    formatStopDate(value: string | number | Date | undefined): string {
        if (!value) {
            return '-';
        }
        const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
        return Number.isNaN(time) ? '-' : this.showDate(time) || '-';
    }

    getStatusSeverity(status: string | undefined): string {
        if (!status) return 'secondary';
        switch (status.toLowerCase()) {
            case 'complete':
            case 'delivered':
            case 'delivery_complete':
            case 'customer_acknowledged':
            case 'completed': return 'success';
            case 'accepted':
            case 'assigned':
            case 'scheduled':
            case 'scheduled for pickup': return 'info';
            case 'at_pickup':
            case 'pickup_complete':
            case 'en_route':
            case 'at_dropoff':
            case 'in transit to yard':
            case 'in transit': return 'warning';
            case 'planned':
            case 'evidence_pending':
            case 'pending schedule':
            case 'pending': return 'danger';
            case 'arrived':
            case 'working':
            case 'at yard': return 'primary';
            case 'exception':
            case 'skipped':
            case 'cancelled': return 'danger';
            default: return 'secondary';
        }
    }

    loadDeliveryActionData() {
        this.shipmentService.getDeliveryActionData(this.shipment.shipmentId!, this.deliveryId).subscribe(data => {
            this.actionData.set(data);
        });
    }
}
